import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Plus,
  Download,
  Eye,

  Users,
  ShoppingCart,
  Receipt,
  FileText,
  X,
  Scale,
  Lock,
  Unlock,
  ShieldAlert,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  CheckCircle,
  Clock,
  Edit,
  Save,
  MessageSquare,
  Info,
  ReceiptText,
  CalendarRange,
  Loader2,
  Send,
  HandCoins,
  WalletCards,
  GraduationCap

} from 'lucide-react';
import { api } from '../services/api';
import { telechargerPdf } from '../utils/telechargerPdf';
import ConfirmDialog from './ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import TableauEncaissements from './TableauEncaissements';
import TableauFraisExamens from './TableauFraisExamens';
import { Toast } from "../components/ui/Toast";
import { LongToast, getLongToastTitle, shouldUseLongToast } from './ui/LongToast';
import JustificatifInput from './JustificatifInput';
import VisionneuseJustificatif from './ui/VisionneuseJustificatif';
import ModalObservationsCaisse from './portefeuille/ModalObservationsCaisse';
import ModalCloturerCaisse from './portefeuille/ModalCloturerCaisse';
import ModalDemanderDotation from './portefeuille/ModalDemanderDotation';
import { useCaisse } from '../contexts/CaisseContext';
import { useFeatures } from '../contexts/FeaturesContext';
import { useHabilitationCaisse, MESSAGE_CAISSE_RESERVEE } from '../hooks/useHabilitationCaisse';

const DASHBOARD_DATE_STORAGE_KEY = 'klasys_portefeuille_selected_date';

// Les deux boutons d'un pied de modal partagent taille ET largeur : un bouton
// d'annulation plus etroit suggere une hierarchie qui n'existe pas.
const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

const PortefeuilleRefonte = () => {
  // ============================================================================
  // ÉTATS DE BASE
  // ============================================================================

  // Le portefeuille sert deux metiers vendus separement : encaisser les
  // scolarites et payer le personnel. Sans la paie, les remunerations, les
  // avances et la carte qui les totalise n'ont plus d'objet — elles
  // afficheraient des zeros la ou l'oeil lit un solde.
  const { estActive } = useFeatures();
  // Ouvrir sa caisse est réservé aux profils comptables ; le serveur refuse sinon
  const { habilite: habiliteCaisse } = useHabilitationCaisse();
  const paieOuverte = estActive('module.paie');
  const examensOuverts = estActive('module.examens');
  const cantineOuverte = estActive('module.cantine');

  // Utilisateur et année scolaire
  const caisseDuJour = useCaisse();
  const [currentUser, setCurrentUser] = useState(null);
  // Le contexte de caisse connaît déjà l'année active : on part d'elle, sans
  // attendre un aller-retour pendant lequel les cartes affichaient 0 FCFA.
  const [anneeActive, setAnneeActive] = useState(caisseDuJour.anneeActive || null);
  // Vrai jusqu'au premier chargement des statistiques : le voile couvre la
  // page dès l'entrée, au lieu de montrer d'abord des cartes vides.
  const [chargementInitial, setChargementInitial] = useState(true);

  // États de chargement
  const [isLoading, setIsLoading] = useState(false);
  // Les deux chargements de statistiques (synthèse et détail) ont chacun leur
  // indicateur : partagé, le premier arrivé l'éteignait pendant que l'autre
  // tournait encore, et le rallumage faisait clignoter les cartes.
  const [chargementStats, setChargementStats] = useState(false);
  const [chargementStatsDetaillees, setChargementStatsDetaillees] = useState(false);
  const isLoadingStats = chargementStats || chargementStatsDetaillees;
  // Chaque chargement de statistiques s'est-il terminé au moins une fois ? Le
  // voile d'entrée attend les DEUX : retiré après le premier, il laissait voir
  // un instant les indicateurs des cartes du second (deux chargements à la suite).
  const [statsPretes, setStatsPretes] = useState({ synthese: false, detail: false });
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  // Indicateurs propres aux dépenses variables et aux salaires : ils pilotaient
  // le voile blanc de toute la page, qui s'éteignait et se rallumait selon
  // l'ordre d'arrivée des réponses (scintillement).
  const [isLoadingDepensesVariables, setIsLoadingDepensesVariables] = useState(false);
  const [isLoadingSalaires, setIsLoadingSalaires] = useState(false);

  // ============================================================================
  // ÉTATS DES FILTRES
  // ============================================================================

  // Deux periodes seulement : `jour` -- qui commande l'ouverture, la cloture et
  // l'encaissement -- et `mois`. `semaine` et `annee` ont ete retires : le mois
  // les remplace comme echelle de consultation.
  const [filterPeriode, setFilterPeriode] = useState('jour');
  // Reference du mois consulte : identifiant de mois scolaire, ou 'AAAA-MM'.
  const [moisChoisi, setMoisChoisi] = useState(null);
  const [moisConsultables, setMoisConsultables] = useState({ moisScolaires: [], horsCalendrier: [] });
  const [filterSection, setFilterSection] = useState('');
  const [filterClasse, setFilterClasse] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [searchEleve, setSearchEleve] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);

  const [filterEncaissementType, setFilterEncaissementType] = useState('all'); // all | inscription | scolaire

  // ✅ NOUVEAUX ÉTATS POUR RECHERCHE ET FILTRE FAMILLE
  const [searchFamilleEleve, setSearchFamilleEleve] = useState(''); // Recherche famille/élève
  const [filterFamille, setFilterFamille] = useState(''); // Filtre par famille_id
  const [famillesDuJour, setFamillesDuJour] = useState([]); // Liste des familles ayant des encaissements ce jour

  // SYSTEM DE TOAST

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [longToast, setLongToast] = useState({
    show: false,
    title: '',
    message: '',
    type: 'error'
  });



  // ============================================================================
  // DONNÉES
  // ============================================================================

  // Données de référence
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classesBySection, setClassesBySection] = useState({});

  // Statistiques
  const [stats, setStats] = useState({
    caisseActuelle: 0,
    totalEncaisse: 0,
    totalDepense: 0,
    balance: 0
  });


  // Ligne ~93, après les autres états de stats
  const [statsDetaillees, setStatsDetaillees] = useState({
    totalEncaisse: 0,
    totalDotations: 0,
    dotationsOperationnelles: 0,
    totalDepense: 0,
    balance: 0,
    caisseActuelle: 0,
    soldeCaissePrincipale: 0,
    fraisFixes: 0,
    fraisScolaires: 0,
    fraisExamens: 0,
    fraisAnnexes: 0,
    fraisTransport: 0,
    fraisCantine: 0,
    depensesFixes: 0,
    depensesVariables: 0,
    depensesSalaires: 0,
    depensesSalairesPersonnel: 0,
    depensesSalairesEnseignants: 0,
    depensesSalairesHybrides: 0,
    depensesAvances: 0,
    depensesAvancesPersonnel: 0,
    depensesAvancesEnseignants: 0,
    depensesAvancesHybrides: 0,
    dotationsBudgetDepenses: 0,
    depensesBudgetFixes: 0,
    depensesBudgetVariables: 0,
    depensesBudgetTotal: 0,
    soldeBudgetDepenses: 0
  });


  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [encaissements, setEncaissements] = useState([]);
  const [depenses, setDepenses] = useState([]);
  const [avancesSalaire, setAvancesSalaire] = useState([]);

  // Dépenses fixes
  const [depensesFixes, setDepensesFixes] = useState([]);
  const [pendingDepenses, setPendingDepenses] = useState([]);
  const [applyingDepenseFixeId, setApplyingDepenseFixeId] = useState(null);


  // États pour les salaires
  const [salaires, setSalaires] = useState([]);
  const [currentPageSalaires, setCurrentPageSalaires] = useState(1);
  const [totalPagesSalaires, setTotalPagesSalaires] = useState(1);
  const [itemsPerPageSalaires] = useState(10);



  // Analyses
  const [analyses, setAnalyses] = useState({
    moyenneDepenseJour: 0,
    moyenneEncaissementJour: 0,
    meilleurJour: null,
    evolution: []
  });

  // ============================================================================
  // ÉTATS PAGINATION
  // ============================================================================

  // ============================================================================
  // ÉTATS PAGINATION (INDIVIDUALISÉS)
  // ============================================================================

  // Pagination Encaissements
  const [currentPageEncaissements, setCurrentPageEncaissements] = useState(1);
  const [totalPagesEncaissements, setTotalPagesEncaissements] = useState(1);
  const [itemsPerPageEncaissements] = useState(10);

  // Pagination Dépenses fixes
  const [currentPageDepensesFixes, setCurrentPageDepensesFixes] = useState(1);
  const [totalPagesDepensesFixes, setTotalPagesDepensesFixes] = useState(1);
  const [itemsPerPageDepensesFixes] = useState(10);

  // Pagination Dépenses variables
  const [currentPageDepensesVariables, setCurrentPageDepensesVariables] = useState(1);
  const [totalPagesDepensesVariables, setTotalPagesDepensesVariables] = useState(1);
  const [itemsPerPageDepensesVariables] = useState(10);

  // Pagination Avances sur salaire
  const [currentPageAvancesSalaire, setCurrentPageAvancesSalaire] = useState(1);
  const [totalPagesAvancesSalaire, setTotalPagesAvancesSalaire] = useState(1);
  const [itemsPerPageAvancesSalaire] = useState(10);

  // Pagination Historique
  const [currentPageHistorique, setCurrentPageHistorique] = useState(1);
  const [totalPagesHistorique, setTotalPagesHistorique] = useState(1);
  const [itemsPerPageHistorique] = useState(20);


  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // ============================================================================
  // ÉTATS CLÔTURE
  // ============================================================================

  // `null` tant que le serveur n'a pas répondu, jamais `non_ouverte`.
  //
  // L'ancienne valeur par défaut AFFIRMAIT que la caisse n'était pas ouverte.
  // D'où le bouton « Ouvrir la caisse » qui apparaissait puis s'effaçait, et
  // l'avertissement de caisse oubliée qui surgissait après coup. L'inconnu et
  // le « pas ouvert » doivent se distinguer.
  const [statutCloture, setStatutCloture] = useState(null);




  // ✅ NOUVEAU : État de la caisse centrale
  const [statutCaisseCentrale, setStatutCaisseCentrale] = useState(null);


  const [showClotureModal, setShowClotureModal] = useState(false);
  const [showOpenCaisseModal, setShowOpenCaisseModal] = useState(false);
  const [recapCloture, setRecapCloture] = useState(null);
  const [dateClotureEnCours, setDateClotureEnCours] = useState(null);
  const [noteCloture, setNoteCloture] = useState('');
  const [motifEcartCloture, setMotifEcartCloture] = useState('');
  const [noteOuverture, setNoteOuverture] = useState('');
  const [montantDeclare, setMontantDeclare] = useState('');
  const [isOpeningCaisse, setIsOpeningCaisse] = useState(false);
  const [isClosingCaisse, setIsClosingCaisse] = useState(false);
  const [showOuvertureExceptionnelleModal, setShowOuvertureExceptionnelleModal] = useState(false);
  const [motifOuvertureExceptionnelle, setMotifOuvertureExceptionnelle] = useState('');
  const [isSendingOuvertureExceptionnelle, setIsSendingOuvertureExceptionnelle] = useState(false);




  // ============================================================================
  // ÉTATS MODALS ET FORMULAIRES
  // ============================================================================

  // Modal dépense variable
  const [showAddDepenseModal, setShowAddDepenseModal] = useState(false);
  const [newDepense, setNewDepense] = useState({
    description: '',
    montant: '',
    justificatif: '',
    justificatif_fichier: null
  });

  // Modal dépense fixe
  // Application d'une depense fixe : la facture DU MOIS.
  //
  // Le modele porte le contrat (le bail) ; il ne prouve pas le paiement de ce
  // mois-ci. La confirmation a son propre dialogue, le champ de fichier devant
  // se rerendre a chaque choix — impossible dans un noeud fige au clic.
  const [depenseFixeAAppliquer, setDepenseFixeAAppliquer] = useState(null);
  const [factureApplication, setFactureApplication] = useState(null);

  // Le justificatif est-il exige ? Reglage d'ecole, relu au chargement.
  const [reglagesDepenses, setReglagesDepenses] = useState({
    justificatif_requis_creation: true,
    justificatif_requis_application: false
  });

  // La piece qu'on regarde, s'il y en a une.
  const [pieceConsultee, setPieceConsultee] = useState(null);

  // Les observations recues sur la caisse remise, et leur reponse.
  const [showObservationsModal, setShowObservationsModal] = useState(false);

  // Le contrat du modele, consulte DEPUIS le dialogue d'application. Etat
  // distinct : cette visionneuse-la est imbriquee dans le dialogue, la ou
  // `pieceConsultee` pilote celle de la page.
  const [voirContratModele, setVoirContratModele] = useState(false);

  // La creation et la modification d'un modele de depense fixe vivent
  // desormais dans DepensesFixesSettingsPage : le modele vaut pour toute
  // l'ecole. Le portefeuille n'en garde que l'application.

  // Modal demande de dotation centrale
  const [showDemandeDotationModal, setShowDemandeDotationModal] = useState(false);
  const [isSendingDemandeDotation, setIsSendingDemandeDotation] = useState(false);
  const [demandeDotation, setDemandeDotation] = useState({
    montant: '',
    usage_dotation: 'budget_depenses',
    objet_dotation: '',
    mois_scolaire_id: '',
    employes_avances: [],
    motif: '',
    justificatif_fichier: null
  });
  const [moisScolairesDotation, setMoisScolairesDotation] = useState([]);

  // Modal notification mensuelle
  const [showMonthlyModal, setShowMonthlyModal] = useState(false);
  const [monthlyDepenses, setMonthlyDepenses] = useState([]);
  const [adjustingDepenses, setAdjustingDepenses] = useState(false);

  // Dialog de confirmation
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState({
    title: '',
    description: '',
    onConfirm: null
  });

  // ============================================================================
  // ÉTATS UI
  // ============================================================================

  const [activeTab, setActiveTab] = useState('encaissements'); // encaissements | depenses | historique
  const [expandedSections, setExpandedSections] = useState(new Set(['encaissements', 'depenses']));
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  // ============================================================================
  // FONCTIONS UTILITAIRES
  // ============================================================================

  // Formater les montants
  const formatMontant = (montant) => {
    return `${Math.round(montant).toLocaleString('fr-FR').replace(/,/g, ' ')} FCFA`;
  };
  const isHybridPortefeuilleItem = (item) => {
    const details = item?.details_salaire || {};
    const typeEmploye = String(details.type_employe || '').toUpperCase();
    const mode = String(details.mode || details.mode_remuneration || '').toUpperCase();

    return Boolean(
      item?.est_hybride ||
      item?.profil_salaire === 'hybride' ||
      details.paiement_hybride === true ||
      typeEmploye === 'HYBRIDE' ||
      mode === 'HYBRIDE'
    );
  };

  const getProfilSalaire = (item) => {
    if (isHybridPortefeuilleItem(item)) return 'hybride';
    if (item?.profil_salaire) return item.profil_salaire;
    if (item?.type_salaire === 'enseignant') return 'enseignant';
    if (item?.type_salaire === 'employe') return 'employe';
    return null;
  };

  const getProfilSalaireLabel = (item) => {
    const profil = getProfilSalaire(item);
    if (profil === 'hybride') return 'Hybride';
    if (profil === 'enseignant') return 'Enseignant';
    if (profil === 'employe') return 'Personnel';
    return item?.employe_role || item?.type_salaire || 'Personnel';
  };

  const getProfilSalaireBadgeClass = (item) => {
    const profil = getProfilSalaire(item);
    if (profil === 'hybride') return 'bg-cyan-100 text-cyan-800';
    if (profil === 'enseignant') return 'bg-blue-100 text-blue-800';
    return 'bg-green-100 text-green-800';
  };

  const getComposanteSalaireLabel = (details = {}) => {
    if (!details.type_personnel) return null;
    return details.type_personnel === 'enseignant' ? 'Composante enseignant' : 'Composante personnel';
  };



  const isDepenseFixeAppliedThisMonth = (depenseFixe) => {
    return Boolean(
      depenseFixe?.appliquee_ce_mois ||
      depenseFixe?.appliqueeCeMois ||
      depenseFixe?.applied_this_month
    );
  };


  const showToast = (message, type = 'success', options = {}) => {
    const text = String(message || '');
    const isLongBusinessMessage = shouldUseLongToast(text, type, options);

    if (isLongBusinessMessage) {
      setToast({ show: false, message: '', type: 'success' });
      setLongToast({
        show: true,
        title: getLongToastTitle(type, options.title),
        message: text,
        type
      });
      return;
    }

    setLongToast({ show: false, title: '', message: '', type: 'error' });
    setToast({ show: true, message: text, type });
  };

  // Les mois consultables : les scolaires de l'annee, puis ceux ou cette
  // gestionnaire a des mouvements hors calendrier. Charges une fois.
  useEffect(() => {
    if (!anneeActive) return;
    let annule = false;
    (async () => {
      const reponse = await api.portefeuille.getMoisConsultables(anneeActive.id);
      if (!annule && reponse?.success) setMoisConsultables(reponse.data);
    })();
    return () => { annule = true; };
  }, [anneeActive]);

  // Le mois consulte, retrouve dans la liste : c'est lui qui porte ses bornes.
  const moisCourantChoisi = React.useMemo(() => {
    const tous = [...moisConsultables.moisScolaires, ...moisConsultables.horsCalendrier];
    return tous.find((m) => m.reference === String(moisChoisi)) || null;
  }, [moisChoisi, moisConsultables]);

  const [impressionEnCours, setImpressionEnCours] = useState(false);

  // Impression de l'etat mensuel, via le socle PDF du projet.
  const imprimerEtatMensuel = async () => {
    if (!moisChoisi || !anneeActive) return;
    const nombre = moisCourantChoisi?.mouvements || 0;
    setImpressionEnCours(true);

    // Le type `loading` ne se ferme pas seul : il tient jusqu'a ce que le
    // fichier arrive. Sur un mois charge, le rendu demande une vingtaine de
    // secondes — sans ce message, on croit a une panne.
    showToast(
      nombre > 200
        ? `Préparation du rapport — ${nombre} mouvements. Cela peut prendre une vingtaine de secondes.`
        : 'Préparation du rapport…',
      'loading'
    );

    try {
      const reponse = await api.portefeuille.exporterEtatMensuelPDF({
        anneeScolaireId: anneeActive.id,
        mois: moisChoisi
      });
      const nom = await telechargerPdf(reponse, 'Etat_mensuel.pdf');
      showToast(`Rapport prêt : ${nom}`);
    } catch (erreur) {
      showToast(erreur.message || "L'impression a échoué", 'error');
    } finally {
      setImpressionEnCours(false);
    }
  };


  /**
   * Pose les bornes de date sur les parametres d'une requete.
   *
   * En mode `jour`, la date selectionnee. En mode `mois`, les DEUX bornes du
   * mois -- c'est ce qui manquait au filtre serveur, qui ne savait lire que
   * « depuis le 1er » et ne pouvait donc pas rendre un mois passe.
   */
  const appliquerBornes = React.useCallback((params) => {
    if (filterPeriode === 'jour') {
      const jour = formatDateForAPI(selectedDate);
      params.date_debut = jour;
      params.date_fin = jour;
    } else if (filterPeriode === 'mois' && moisCourantChoisi) {
      params.date_debut = moisCourantChoisi.dateDebut;
      params.date_fin = moisCourantChoisi.dateFin;
    }
    return params;
  }, [filterPeriode, selectedDate, moisCourantChoisi, moisChoisi]);

  // Obtenir la date du jour OU la date sélectionnée si en mode jour
  const getToday = () => {
    if (filterPeriode === 'jour') {
      return formatDateForAPI(selectedDate);
    }
    return new Date().toISOString().split('T')[0];
  };


  const formatDateForAPI = (date) => {
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  };

  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  useEffect(() => {
    localStorage.setItem(DASHBOARD_DATE_STORAGE_KEY, formatDateForAPI(selectedDate));
  }, [selectedDate]);

  // Tous les mois consultables, dans l'ordre du selecteur.
  const tousLesMois = React.useMemo(
    () => [...moisConsultables.moisScolaires, ...moisConsultables.horsCalendrier],
    [moisConsultables]
  );

  /**
   * Deplacement d'un cran dans la liste des mois.
   *
   * On se deplace dans la LISTE, pas dans le calendrier gregorien : l'annee
   * scolaire de csap1 saute de juin a octobre, et ajouter un mois a une date
   * proposerait juillet, aout et septembre, qui n'existent pas pour elle.
   */
  /**
   * Consulte-t-on un mois ecoule ?
   *
   * Toute ecriture serait datee d'aujourd'hui et partirait dans le mois
   * courant, invisible sur l'ecran regarde. On retire donc les commandes
   * plutot que de les griser : elles ne s'activeront pas depuis cette vue.
   */
  const consulteMoisEcoule = React.useMemo(() => {
    if (filterPeriode !== 'mois' || !moisCourantChoisi) return false;
    const aujourdhui = formatDateForAPI(new Date());
    return aujourdhui < moisCourantChoisi.dateDebut || aujourdhui > moisCourantChoisi.dateFin;
  }, [filterPeriode, moisCourantChoisi]);

  const naviguerMois = (pas) => {
    if (!moisChoisi || tousLesMois.length === 0) return;
    const position = tousLesMois.findIndex((m) => m.reference === String(moisChoisi));
    if (position === -1) return;
    const cible = tousLesMois[position + pas];
    if (cible) setMoisChoisi(cible.reference);
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
    setFilterPeriode('jour');
  };


  // Obtenir l'icône selon le type de transaction
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'encaissement':
        return <TrendingUp size={20} className="text-green-600" />;
      case 'depense':
        return <ShoppingCart size={20} className="text-orange-600" />;
      case 'salaire':
        return <Users size={20} className="text-blue-600" />;
      case 'avance_salaire':
        return <HandCoins size={20} className="text-amber-600" />;
      default:
        return <TrendingUp size={20} className="text-gray-600" />;
    }
  };

  // Obtenir le badge selon le type
  const getTransactionBadge = (transactionOrType) => {
    const transaction = typeof transactionOrType === 'object' ? transactionOrType : { type: transactionOrType };
    const { type } = transaction;
    const isHybrid = isHybridPortefeuilleItem(transaction);
    const configs = {
      encaissement: {
        bg: "bg-green-100",
        text: "text-green-800",
        label: "Encaissement"
      },
      depense: {
        bg: "bg-orange-100",
        text: "text-orange-800",
        label: "Dépense"
      },
      salaire: {
        bg: isHybrid ? "bg-cyan-100" : "bg-blue-100",
        text: isHybrid ? "text-cyan-800" : "text-blue-800",
        label: isHybrid ? "Salaire hybride" : "Salaire"
      },
      avance_salaire: {
        bg: isHybrid ? "bg-cyan-100" : "bg-amber-100",
        text: isHybrid ? "text-cyan-800" : "text-amber-800",
        label: isHybrid ? "Avance hybride" : "Avance salaire"
      }
    };

    const config = configs[type] || configs.encaissement;
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };



  // Toggle section expansion
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionName)) {
        newSet.delete(sectionName);
      } else {
        newSet.add(sectionName);
      }
      return newSet;
    });
  };

  // ============================================================================
  // CHARGEMENT INITIAL
  // ============================================================================

  // Charger l'utilisateur connecté
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
  }, []);

  // Charger l'année scolaire active
  useEffect(() => {
    // Déjà connue du contexte de caisse : rien à redemander.
    if (caisseDuJour.anneeActive) return;
    const fetchAnneeActive = async () => {
      try {
        const response = await api.anneesScolaires.getActive();
        if (response.success && response.data) {
          setAnneeActive(response.data);
        } else {
          setChargementInitial(false);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération de l'année scolaire active:", error);
        setChargementInitial(false);
      }
    };
    fetchAnneeActive();
  }, []);

  /**
   * Applique la dépense fixe retenue, avec la facture du mois s'il y en a une.
   *
   * Le montant inscrit n'est pas celui d'ici : le serveur relit le modèle. Ce
   * qui part d'ici, c'est l'identifiant et le fichier.
   */
  const appliquerDepenseFixe = async () => {
    const depenseFixe = depenseFixeAAppliquer;
    if (!depenseFixe || applyingDepenseFixeId === depenseFixe.id) return;

    try {
      setApplyingDepenseFixeId(depenseFixe.id);

      const result = await api.depensesFixes.appliquerMensuelles(
        [{ id: depenseFixe.id }],
        anneeActive?.id,
        filterPeriode === 'mois' ? moisChoisi : null,
        factureApplication
      );

      if (result.success) {
        showToast(
          `Dépense : ${depenseFixe.nom}\n` +
          `Montant : ${formatMontant(result.data?.montant_total || depenseFixe.montant)}\n\n` +
          `La dépense a été ajoutée au portefeuille. Le budget dépenses a été mis à jour.`,
          'success',
          { long: true, title: 'Dépense fixe appliquée' }
        );
        setDepenseFixeAAppliquer(null);
        setFactureApplication(null);
        setVoirContratModele(false);
        await reloadAllData();
      } else {
        showToast(
          result.message || "Erreur lors de l'application de la dépense fixe.",
          'error',
          { long: true, title: 'Application refusée' }
        );
      }
    } catch (error) {
      console.error('Erreur application dépense fixe :', error);
      showToast(
        error.message || "Erreur lors de l'application de la dépense fixe.",
        'error',
        { long: true, title: 'Application refusée' }
      );
    } finally {
      setApplyingDepenseFixeId(null);
    }
  };

  // Les reglages de justificatif, une fois l'annee connue.
  //
  // Ils sont relus ici plutot que devines : la gestionnaire doit savoir AVANT
  // d'ouvrir le formulaire si le fichier sera exige, sinon elle decouvre le
  // refus apres coup. Le serveur les revérifie de toute facon.
  useEffect(() => {
    if (!anneeActive) return;
    let annule = false;

    (async () => {
      const reponse = await api.depensesFixes.getParametres(anneeActive.id);
      if (annule || !reponse?.success || !reponse.data) return;
      setReglagesDepenses({
        justificatif_requis_creation: Boolean(reponse.data.justificatif_requis_creation),
        justificatif_requis_application: Boolean(reponse.data.justificatif_requis_application)
      });
    })();

    return () => { annule = true; };
  }, [anneeActive]);

  // Charger les sections et classes
  useEffect(() => {
    const fetchSectionsEtClasses = async () => {
      try {
        // Charger les sections
        const sectionsResponse = await api.sections.getAll();
        if (sectionsResponse.success) {
          setSections(sectionsResponse.data);
        }

        // Charger toutes les classes
        const classesData = await api.classes.getAll();
        if (Array.isArray(classesData)) {
          setClasses(classesData);

          // Grouper les classes par section
          const grouped = classesData.reduce((acc, classe) => {
            const sectionId = classe.section_id || 'sans_section';
            if (!acc[sectionId]) {
              acc[sectionId] = [];
            }
            acc[sectionId].push(classe);
            return acc;
          }, {});
          setClassesBySection(grouped);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des sections/classes:", error);
      }
    };

    fetchSectionsEtClasses();
  }, []);


  // Synchroniser la date sélectionnée avec le filtre de période
  // `reloadAllData` change d'identite a chaque rendu : le mettre en dependance
  // relancerait tout en boucle. On garde donc une reference vers la DERNIERE
  // version, plutot que la fermeture perimee que cet effet capturait —
  // laquelle interrogeait le serveur pour la date qu'on venait de quitter.
  const reloadAllDataRef = useRef(null);

  useEffect(() => {
    if (statsPretes.synthese && statsPretes.detail) setChargementInitial(false);
  }, [statsPretes]);

  // Plus de rechargement complet automatique ici : les effets de chaque
  // chargement (plus bas) réagissent déjà à l'année, à la date et au mois. Les
  // deux ensemble envoyaient chaque requête deux fois, et allumaient deux fois
  // chaque indicateur. `reloadAllData` sert aux rafraîchissements après une
  // action (ouverture, clôture, dépense...).



  // ============================================================================
  // FONCTIONS DE CHARGEMENT DES DONNÉES
  // ============================================================================

  // Vérifier le statut de clôture du jour
  // Le statut de la caisse : le contexte pour aujourd'hui, le serveur pour les
  // jours passés.
  //
  // Le contexte vit au-dessus des écrans et survit à la navigation : revenir sur
  // le portefeuille n'oblige plus à réinterroger le serveur, l'état est déjà là.
  // Les dates antérieures, elles, ne concernent que cet écran — le contexte ne
  // décrit que la journée en cours.
  // La date dont on attend l'etat. Toute reponse portant une autre date est
  // perimee : l'ecran a change entre-temps, et l'afficher ferait clignoter un
  // etat qui n'est pas celui du jour regarde.
  const dateStatutAttendue = useRef(null);

  const rafraichirCaisseDuJour = caisseDuJour.rafraichir;

  const checkStatutCloture = useCallback(async () => {
    if (!anneeActive) return;

    const dateStatut = filterPeriode === 'jour'
      ? formatDateForAPI(selectedDate)
      : formatDateForAPI(new Date());
    const estAujourdhui = dateStatut === formatDateForAPI(new Date());

    dateStatutAttendue.current = dateStatut;

    if (estAujourdhui) {
      await rafraichirCaisseDuJour();
      return;
    }

    // Repartir de l'inconnu avant d'interroger : sans cela l'ecran garde l'etat
    // de la date PRECEDENTE le temps de la reponse. Tous les drapeaux passent
    // a faux, donc rien ne s'affiche — ce qui vaut mieux que d'afficher l'etat
    // d'un autre jour. Le premier chargement etait deja traite, pas celui-ci.
    setStatutCloture(null);

    try {
      const response = await api.portefeuille.getStatutCloture(dateStatut, anneeActive.id);

      // La reponse d'une date qu'on ne regarde plus est jetee.
      if (dateStatutAttendue.current !== dateStatut) return;

      if (response.success) setStatutCloture(response.data);
    } catch (error) {
      console.error("Erreur vérification statut clôture:", error);
    }
    // `rafraichirCaisseDuJour` et non `caisseDuJour` : l'objet du contexte est
    // recréé à chaque mise à jour, donc à chaque réponse. En dépendre relançait
    // cette vérification, qui redemandait l'état, qui recréait l'objet : l'état
    // de la caisse était interrogé en boucle tant qu'on regardait aujourd'hui.
  }, [anneeActive, filterPeriode, selectedDate, rafraichirCaisseDuJour, moisChoisi]);

  // Ce que le contexte sait d'aujourd'hui se reporte sur l'écran, tant qu'on
  // regarde bien aujourd'hui.
  useEffect(() => {
    const dateAffichee = filterPeriode === 'jour'
      ? formatDateForAPI(selectedDate)
      : formatDateForAPI(new Date());
    if (dateAffichee !== formatDateForAPI(new Date())) return;
    // Le contexte ne connait qu'AUJOURD'HUI : ne rien reporter si l'ecran
    // attend une autre date.
    if (dateStatutAttendue.current && dateStatutAttendue.current !== dateAffichee) return;
    if (caisseDuJour.charge) setStatutCloture(caisseDuJour.statut);
  }, [caisseDuJour.charge, caisseDuJour.statut, filterPeriode, selectedDate, moisChoisi]);




  // Vérifier le statut dans la caisse centrale
  const checkStatutCaisseCentrale = useCallback(async () => {
    if (!anneeActive || !statutCloture?.est_cloturee) return;

    try {
      // SA remise du jour uniquement. La liste complète de la caisse centrale
      // est réservée par dérogation ; elle rendait en outre la première caisse
      // du jour venue, qui pouvait être celle d'un autre gestionnaire.
      const response = await api.caisseCentrale.getMaCaisse({
        date: getToday(),
        annee_scolaire_id: anneeActive.id
      });

      if (response.success && response.data) {
        setStatutCaisseCentrale(response.data);
      }
    } catch (error) {
      console.error("Erreur vérification caisse centrale:", error);
    }
  }, [anneeActive, statutCloture?.est_cloturee]);




  // Charger les statistiques
  const loadStats = useCallback(async () => {
    if (!anneeActive) return;

    try {
      setChargementStats(true);

      const params = {
        periode: filterPeriode,
        annee_scolaire_id: anneeActive.id
      };

      // ✅ AJOUT : Si période = jour, envoyer la date sélectionnée
      appliquerBornes(params);

      // Ajouter filtres section/classe si actifs
      if (filterSection) params.section_id = filterSection;
      if (filterClasse) params.classe_id = filterClasse;

      const response = await api.portefeuille.getStatsFiltrees(params);

      if (response.success) {
        setStats(response.data);
      }
      setChargementStats(false);
      setStatsPretes((prev) => (prev.synthese ? prev : { ...prev, synthese: true }));
    } catch (error) {
      console.error("Erreur chargement stats:", error);
      setChargementStats(false);
      setStatsPretes((prev) => (prev.synthese ? prev : { ...prev, synthese: true }));
    }
  }, [filterPeriode, filterSection, filterClasse, anneeActive, selectedDate, moisChoisi]); // ✅ Ajouter selectedDate




  // Ligne ~340, après la fonction loadStats
  const loadStatsDetaillees = useCallback(async () => {
    if (!anneeActive) return;

    try {
      setChargementStatsDetaillees(true);

      const params = {
        periode: filterPeriode,
        annee_scolaire_id: anneeActive.id
      };

      // ✅ AJOUT : Si période = jour, envoyer la date sélectionnée
      appliquerBornes(params);

      if (filterSection) params.section_id = filterSection;
      if (filterClasse) params.classe_id = filterClasse;

      const response = await api.portefeuille.getStatsDetaillees(params);

      if (response.success) {
        setStatsDetaillees(response.data);
      }
      setChargementStatsDetaillees(false);
      setStatsPretes((prev) => (prev.detail ? prev : { ...prev, detail: true }));
    } catch (error) {
      console.error("Erreur chargement stats détaillées:", error);
      setChargementStatsDetaillees(false);
      setStatsPretes((prev) => (prev.detail ? prev : { ...prev, detail: true }));
    }
  }, [filterPeriode, filterSection, filterClasse, anneeActive, selectedDate, moisChoisi]); // ✅ Ajouter selectedDate

  // ============================================================================
  // FONCTIONS DE CHARGEMENT INDIVIDUALISÉES
  // ============================================================================

  // Charger les ENCAISSEMENTS
  const loadEncaissements = useCallback(async () => {
    if (!anneeActive) return;

    try {
      setIsLoadingTransactions(true);

      const params = {
        periode: filterPeriode,
        annee_scolaire_id: anneeActive.id,
        page: currentPageEncaissements,
        limit: itemsPerPageEncaissements,
        type: 'encaissement' // Forcer type encaissement
      };

      // ✅ AJOUT : Si période = jour, envoyer la date sélectionnée
      appliquerBornes(params);

      if (filterSection) params.section_id = filterSection;
      if (filterClasse) params.classe_id = filterClasse;
      // Recherche élève (ancien filtre)
      if (searchEleve && searchEleve.length >= 2) params.search_eleve = searchEleve;

      // ✅ NOUVEAU : Recherche famille/élève combinée
      if (searchFamilleEleve && searchFamilleEleve.length >= 2) {
        params.search_eleve = searchFamilleEleve; // Le backend cherche déjà dans nom élève
        // On filtrera côté client pour les familles
      }

      // ✅ NOUVEAU : Filtre par famille_id
      if (filterFamille) {
        params.famille_id = filterFamille;
      }
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;

      console.log('🔍 [loadEncaissements] Params:', params);

      const response = await api.portefeuille.getTransactionsFiltrees(params);

      if (response.success) {
        let enc = response.data;



        // ✅ Filtre côté client pour la recherche famille/élève
        if (searchFamilleEleve && searchFamilleEleve.length >= 2) {
          const searchLower = searchFamilleEleve.toLowerCase();
          enc = enc.filter(e => {
            const nomEleve = `${e.eleve_prenom || ''} ${e.eleve_nom || ''}`.toLowerCase();
            const nomFamille = (e.famille_nom || '').toLowerCase();
            return nomEleve.includes(searchLower) || nomFamille.includes(searchLower);
          });
        }

        // ✅ Filtre par famille_id
        if (filterFamille) {
          enc = enc.filter(e => e.famille_id && e.famille_id.toString() === filterFamille);
        }


        // Appliquer le filtre de sous-type
        if (filterEncaissementType === 'inscription') {
          enc = enc.filter(e => {
            const desc = (e.description || '').toLowerCase();
            return desc.includes('inscription') ||
              desc.includes('réinscription') ||
              desc.includes('reinscription');
          });
        } else if (filterEncaissementType === 'scolaire') {
          enc = enc.filter(e => {
            const desc = (e.description || '').toLowerCase();
            return !(desc.includes('inscription') ||
              desc.includes('réinscription') ||
              desc.includes('reinscription'));
          });
        }

        setEncaissements(enc);
        console.log('✅ [loadEncaissements] Chargés:', enc.length);

        if (response.pagination) {
          setTotalPagesEncaissements(response.pagination.total_pages || 1);
        }
      }

      setIsLoadingTransactions(false);
    } catch (error) {
      console.error('❌ [loadEncaissements] Erreur:', error);
      setIsLoadingTransactions(false);
    }
  }, [
    filterPeriode,
    filterSection,
    filterClasse,
    searchEleve,
    dateDebut,
    dateFin,
    currentPageEncaissements,
    itemsPerPageEncaissements,
    anneeActive,
    filterEncaissementType,
    selectedDate, // ✅ Ajouter selectedDate
    moisChoisi
  ]);


  // Charger la liste des familles ayant des encaissements pour la date sélectionnée
  const loadFamillesDuJour = useCallback(async () => {
    if (!anneeActive) return;

    try {
      const params = {
        periode: filterPeriode,
        annee_scolaire_id: anneeActive.id,
        type: 'encaissement'
      };

      // Si période = jour, envoyer la date sélectionnée
      appliquerBornes(params);

      console.log('🔍 [loadFamillesDuJour] Params:', params);

      // Récupérer toutes les transactions (sans pagination)
      const response = await api.portefeuille.getTransactionsFiltrees({
        ...params,
        page: 1,
        limit: 1000 // Charger toutes les transactions
      });

      if (response.success) {
        // Extraire les familles uniques
        const famillesMap = new Map();

        response.data.forEach(transaction => {
          if (transaction.famille_id && transaction.famille_nom) {
            if (!famillesMap.has(transaction.famille_id)) {
              famillesMap.set(transaction.famille_id, {
                id: transaction.famille_id,
                nom: transaction.famille_nom,
                nb_encaissements: 0,
                total: 0
              });
            }

            const famille = famillesMap.get(transaction.famille_id);
            famille.nb_encaissements++;
            famille.total += parseFloat(transaction.montant);
          }
        });

        // Convertir en tableau trié par nom
        const famillesArray = Array.from(famillesMap.values()).sort((a, b) =>
          a.nom.localeCompare(b.nom)
        );

        setFamillesDuJour(famillesArray);
        console.log('✅ [loadFamillesDuJour] Familles chargées:', famillesArray.length);
      }
    } catch (error) {
      console.error('❌ [loadFamillesDuJour] Erreur:', error);
    }
  }, [filterPeriode, anneeActive, selectedDate, moisChoisi]);




  // Charger les DÉPENSES FIXES
  const loadDepensesFixes = useCallback(async () => {
    if (!anneeActive) return;

    try {
      const response = await api.depensesFixes.getAll(anneeActive.id);
      if (response.success) {
        // Le portefeuille n'applique que les modèles ACTIFS : montrer les
        // autres n'offrirait qu'une ligne sans action. Le filtre passe avant
        // le découpage, sans quoi les pages compteraient des lignes absentes.
        // La gestion des modèles vit désormais dans les Paramètres.
        const allDepensesFixes = (response.data || []).filter(d => d.statut === 'actif');
        const startIndex = (currentPageDepensesFixes - 1) * itemsPerPageDepensesFixes;
        const endIndex = startIndex + itemsPerPageDepensesFixes;
        const paginatedDepensesFixes = allDepensesFixes.slice(startIndex, endIndex);

        setDepensesFixes(paginatedDepensesFixes);
        setTotalPagesDepensesFixes(Math.ceil(allDepensesFixes.length / itemsPerPageDepensesFixes));

        console.log('✅ [loadDepensesFixes] Chargées:', paginatedDepensesFixes.length, '/', allDepensesFixes.length);
      }
    } catch (error) {
      console.error('❌ [loadDepensesFixes] Erreur:', error);
    }
  }, [anneeActive, currentPageDepensesFixes, itemsPerPageDepensesFixes, selectedDate]); // ✅ Ajouter selectedDate



  // Charger les DÉPENSES VARIABLES
  const loadDepensesVariables = useCallback(async () => {
    if (!anneeActive) return;

    try {
      setIsLoadingDepensesVariables(true);

      const params = {
        periode: filterPeriode,
        annee_scolaire_id: anneeActive.id,
        page: currentPageDepensesVariables,
        limit: itemsPerPageDepensesVariables,
        type: 'depense' // Forcer type depense
      };

      if (filterPeriode === 'jour') {
        // Lecture d'enveloppe : cumul depuis le debut de l'annee jusqu'a la
        // date choisie, budget depenses seul. Pas de borne de debut -- c'est
        // ce qui en fait un cumul, et non le solde d'une journee.
        params.depense_scope = 'variable_budget_cumulatif';
        params.date_fin = formatDateForAPI(selectedDate);
      } else if (filterPeriode === 'mois' && moisCourantChoisi) {
        // Lecture de periode : ce qui a ete depense DANS ce mois, toutes
        // sources confondues. Bornes des deux cotes.
        params.depense_scope = 'variable';
        params.date_debut = moisCourantChoisi.dateDebut;
        params.date_fin = moisCourantChoisi.dateFin;
      } else {
        params.depense_scope = 'variable';
      }

      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;

      console.log('🔍 [loadDepensesVariables] Params:', params);

      const response = await api.portefeuille.getTransactionsFiltrees(params);

      if (response.success) {
        const dep = response.data.filter(t => t.type === 'depense' && !t.depense_fixe_id);
        setDepenses(dep);
        console.log('✅ [loadDepensesVariables] Chargées:', dep.length);

        if (response.pagination) {
          setTotalPagesDepensesVariables(response.pagination.total_pages || 1);
        }
      }

      setIsLoadingDepensesVariables(false);
    } catch (error) {
      console.error('❌ [loadDepensesVariables] Erreur:', error);
      setIsLoadingDepensesVariables(false);
    }
  }, [
    filterPeriode,
    dateDebut,
    dateFin,
    currentPageDepensesVariables,
    itemsPerPageDepensesVariables,
    anneeActive,
    selectedDate, // ✅ Ajouter selectedDate
    moisChoisi
  ]);



  // Charger les AVANCES SUR SALAIRE
  const loadAvancesSalaire = useCallback(async () => {
    // Sans la paie, la sous-section n'est pas rendue : ne rien aller chercher.
    if (!anneeActive || !paieOuverte) return;

    try {
      setIsLoadingTransactions(true);

      const params = {
        periode: filterPeriode,
        annee_scolaire_id: anneeActive.id,
        page: currentPageAvancesSalaire,
        limit: itemsPerPageAvancesSalaire,
        type: 'avance_salaire'
      };

      appliquerBornes(params);

      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;

      const response = await api.portefeuille.getTransactionsFiltrees(params);

      if (response.success) {
        setAvancesSalaire((response.data || []).filter(t => t.type === 'avance_salaire'));

        if (response.pagination) {
          setTotalPagesAvancesSalaire(response.pagination.total_pages || 1);
        }
      }

      setIsLoadingTransactions(false);
    } catch (error) {
      console.error('Erreur chargement avances sur salaire:', error);
      setIsLoadingTransactions(false);
    }
  }, [
    filterPeriode,
    dateDebut,
    dateFin,
    currentPageAvancesSalaire,
    itemsPerPageAvancesSalaire,
    anneeActive,
    paieOuverte,
    selectedDate, moisChoisi]);


  // Charger les SALAIRES
  const loadSalaires = useCallback(async () => {
    if (!anneeActive || !paieOuverte) return;

    try {
      setIsLoadingSalaires(true);

      const params = {
        periode: filterPeriode,
        annee_scolaire_id: anneeActive.id,
        page: currentPageSalaires,
        limit: itemsPerPageSalaires
      };

      // Si période = jour, envoyer la date sélectionnée
      appliquerBornes(params);

      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;

      console.log('🔍 [loadSalaires] Params:', params);

      const response = await api.portefeuille.getSalaires(params);

      if (response.success) {
        setSalaires(response.data);
        console.log('✅ [loadSalaires] Chargés:', response.data.length);

        if (response.pagination) {
          setTotalPagesSalaires(response.pagination.total_pages || 1);
        }
      }

      setIsLoadingSalaires(false);
    } catch (error) {
      console.error('❌ [loadSalaires] Erreur:', error);
      setIsLoadingSalaires(false);
    }
  }, [
    filterPeriode,
    dateDebut,
    dateFin,
    currentPageSalaires,
    itemsPerPageSalaires,
    anneeActive,
    paieOuverte,
    selectedDate, moisChoisi]);





  // Charger l'HISTORIQUE COMPLET
  const loadHistorique = useCallback(async () => {
    if (!anneeActive) return;

    try {
      setIsLoadingTransactions(true);

      const params = {
        periode: filterPeriode,
        annee_scolaire_id: anneeActive.id,
        page: currentPageHistorique,
        limit: itemsPerPageHistorique
      };

      // ✅ AJOUT : Si période = jour, envoyer la date sélectionnée
      appliquerBornes(params);

      // Tous les filtres s'appliquent à l'historique
      if (filterType !== 'all' && filterType !== '') params.type = filterType;
      if (filterSection) params.section_id = filterSection;
      if (filterClasse) params.classe_id = filterClasse;
      if (searchEleve && searchEleve.length >= 2) params.search_eleve = searchEleve;
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;

      console.log('🔍 [loadHistorique] Params:', params);

      const response = await api.portefeuille.getTransactionsFiltrees(params);

      if (response.success) {
        setTransactions(response.data);
        console.log('✅ [loadHistorique] Chargées:', response.data.length);

        if (response.pagination) {
          setTotalPagesHistorique(response.pagination.total_pages || 1);
        }
      }

      setIsLoadingTransactions(false);
    } catch (error) {
      console.error('❌ [loadHistorique] Erreur:', error);
      setIsLoadingTransactions(false);
    }
  }, [
    filterPeriode,
    filterType,
    filterSection,
    filterClasse,
    searchEleve,
    dateDebut,
    dateFin,
    currentPageHistorique,
    itemsPerPageHistorique,
    anneeActive,
    selectedDate, // ✅ Ajouter selectedDate
    moisChoisi
  ]);


  // Fonction globale de rechargement
  const reloadAllData = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([
      checkStatutCloture(),
      loadStats(),
      loadStatsDetaillees(),
      loadEncaissements(),
      loadDepensesFixes(),
      loadDepensesVariables(),
      loadAvancesSalaire(),
      loadSalaires(),
      loadHistorique(),
      loadFamillesDuJour()
    ]);

    // ✅ Vérifier aussi la caisse centrale si la caisse est clôturée
    if (statutCloture?.est_cloturee) {
      await checkStatutCaisseCentrale();
    }

    setIsLoading(false);
  }, [checkStatutCloture, loadStats, loadStatsDetaillees, loadEncaissements, loadDepensesFixes, loadDepensesVariables, loadAvancesSalaire, loadSalaires, loadHistorique, loadFamillesDuJour, statutCloture?.est_cloturee, checkStatutCaisseCentrale]);


  // ============================================================================
  // DEMANDE DE DOTATION A LA CAISSE CENTRALE
  // ============================================================================

  // Mois scolaires pour la dotation destinee aux avances. Le mois civil est
  // derive de date_debut, comme cote serveur.
  useEffect(() => {
    if (!showDemandeDotationModal || moisScolairesDotation.length > 0) return;

    const chargerMois = async () => {
      try {
        const response = await api.moisScolaires.getAll();
        if (!response?.success) return;
        // mois_civil et annee_civile sont calcules par le serveur depuis
        // date_debut : les deriver ici en JS decalerait d'un jour selon le fuseau.
        setMoisScolairesDotation(response.data || []);
      } catch (error) {
        console.error('Erreur chargement mois scolaires:', error);
      }
    };

    chargerMois();
  }, [showDemandeDotationModal, moisScolairesDotation.length]);
  const handleDemanderDotation = async () => {
    if (!demandeDotation.montant || parseFloat(demandeDotation.montant) <= 0) {
      showToast('Le montant demande doit etre superieur a 0 FCFA.', 'error');
      return;
    }
    if (!demandeDotation.motif.trim()) {
      showToast('Le motif de la demande est obligatoire.', 'error');
      return;
    }
    if (!demandeDotation.justificatif_fichier) {
      showToast('Le justificatif est obligatoire pour une demande de dotation.', 'error');
      return;
    }

    // Une dotation destinee aux avances doit designer son mois et ses employes :
    // c'est ce qui borne le montant cote serveur.
    const pourAvances = demandeDotation.usage_dotation === 'principale';
    if (pourAvances) {
      if (!demandeDotation.mois_scolaire_id) {
        showToast('Selectionnez le mois concerne par cette dotation d avances.', 'error');
        return;
      }
      if (!demandeDotation.employes_avances?.length) {
        showToast('Selectionnez au moins un employe concerne par cette dotation d avances.', 'error');
        return;
      }
    }

    try {
      setIsSendingDemandeDotation(true);
      showToast('Envoi de la demande de dotation...', 'loading');

      const moisChoisi = moisScolairesDotation.find(
        (mois) => String(mois.id) === String(demandeDotation.mois_scolaire_id)
      );

      const response = await api.portefeuille.demanderDotation({
        montant: parseFloat(demandeDotation.montant),
        usage_dotation: demandeDotation.usage_dotation,
        objet_dotation: demandeDotation.objet_dotation || undefined,
        motif: demandeDotation.motif,
        justificatif_fichier: demandeDotation.justificatif_fichier,
        annee_scolaire_id: anneeActive?.id,
        ...(pourAvances ? {
          mois_scolaire_id: demandeDotation.mois_scolaire_id,
          mois: moisChoisi?.mois_civil,
          annee: moisChoisi?.annee_civile,
          employes_avances: (demandeDotation.employes_avances || []).map((employeId) => ({
            employe_id: employeId,
            type_employe: demandeDotation.type_employe || 'FIXE'
          }))
        } : {})
      });

      if (response.success) {
        showToast('Demande de dotation envoyee a la caisse centrale.', 'success');
        setDemandeDotation({
          montant: '',
          usage_dotation: 'budget_depenses',
          objet_dotation: '',
          mois_scolaire_id: '',
          employes_avances: [],
          motif: '',
          justificatif_fichier: null
        });
        setShowDemandeDotationModal(false);
        await reloadAllData();
      } else {
        showToast(response.message || "Erreur lors de l'envoi de la demande", 'error');
      }
    } catch (error) {
      console.error('Erreur demande dotation:', error);
      showToast(error.message || "Erreur lors de l'envoi de la demande", 'error');
    } finally {
      setIsSendingDemandeDotation(false);
    }
  };




  // ============================================================================
  // EFFETS DE CHARGEMENT INDIVIDUALISÉS
  // ============================================================================


  // Charger encaissements (+ recharger quand filtre famille change)
  useEffect(() => {
    if (anneeActive) {
      loadEncaissements();
    }
  }, [anneeActive, loadEncaissements, filterFamille]); // ✅ Ajouter filterFamille

  // Charger les familles du jour
  useEffect(() => {
    if (anneeActive) {
      loadFamillesDuJour();
    }
  }, [anneeActive, loadFamillesDuJour]);

  // Charger dépenses fixes
  useEffect(() => {
    if (anneeActive) {
      loadDepensesFixes();
    }
  }, [anneeActive, loadDepensesFixes]);

  // Charger dépenses variables
  useEffect(() => {
    if (anneeActive) {
      loadDepensesVariables();
    }
  }, [anneeActive, loadDepensesVariables]);

  // Charger avances sur salaire
  useEffect(() => {
    if (anneeActive) {
      loadAvancesSalaire();
    }
  }, [anneeActive, loadAvancesSalaire]);


  // Charger historique
  useEffect(() => {
    if (anneeActive) {
      loadHistorique();
    }
  }, [anneeActive, loadHistorique]);

  // Charger stats et analyses (inchangé)
  useEffect(() => {
    if (anneeActive) {
      loadStats();
      loadStatsDetaillees();
    }
  }, [anneeActive, loadStats]);

  // Vérifier statut clôture (inchangé)
  useEffect(() => {
    if (anneeActive) {
      checkStatutCloture();
    }
  }, [anneeActive, checkStatutCloture]);


  // Réinitialiser les pages lors du changement de filtres
  useEffect(() => {
    setCurrentPageEncaissements(1);
    setCurrentPageDepensesVariables(1);
    setCurrentPageAvancesSalaire(1);
    setCurrentPageHistorique(1);
  }, [
    filterPeriode,
    filterType,
    filterSection,
    filterClasse,
    searchEleve,
    dateDebut,
    dateFin,
    searchFamilleEleve, // ✅ AJOUTER
    filterFamille // ✅ AJOUTER
  ]);

  // La reference pointe toujours vers la derniere version : l'effet de
  // changement de date l'appelle sans la mettre en dependance.
  reloadAllDataRef.current = reloadAllData;


  // Vérifier statut caisse centrale (après clôture)
  useEffect(() => {
    if (anneeActive && statutCloture?.est_cloturee) {
      checkStatutCaisseCentrale();
    }
  }, [anneeActive, statutCloture?.est_cloturee, checkStatutCaisseCentrale]);


  // Charger salaires
  useEffect(() => {
    if (anneeActive) {
      loadSalaires();
    }
  }, [anneeActive, loadSalaires]);




  // ============================================================================
  // GESTION DES FILTRES
  // ============================================================================

  // Réinitialiser tous les filtres
  const resetFilters = () => {
    setFilterPeriode('jour');
    setFilterSection('');
    setFilterClasse('');
    setFilterType('all');
    setSearchEleve('');
    setDateDebut('');
    setDateFin('');
    setCurrentPage(1);
  };

  // Gérer le changement de section (réinitialiser la classe)
  const handleSectionChange = (sectionId) => {
    setFilterSection(sectionId);
    setFilterClasse(''); // Réinitialiser la classe
  };

  const handleOpenCaisse = async () => {
    if (caissePrecedenteNonCloturee) {
      showToast(`La caisse du ${dateCaisseOubliee} doit d'abord etre cloturee.`, 'error');
      return;
    }

    try {
      setIsOpeningCaisse(true);
      showToast('Ouverture de la caisse en cours...', 'loading');

      const response = await api.portefeuille.ouvrirCaisse(
        getToday(),
        anneeActive?.id,
        0,
        noteOuverture
      );

      if (response.success) {
        showToast('Caisse ouverte pour la journée.', 'success');
        setShowOpenCaisseModal(false);
        setNoteOuverture('');
        await reloadAllData();
      } else {
        showToast(response.message || 'Erreur lors de l\'ouverture de la caisse', 'error');
      }
    } catch (error) {
      console.error('Erreur ouverture caisse:', error);
      showToast(error.message || 'Erreur lors de l\'ouverture de la caisse', 'error');
    } finally {
      setIsOpeningCaisse(false);
    }
  };

  const handleDemandeOuvertureExceptionnelle = async () => {
    if (!motifOuvertureExceptionnelle.trim()) {
      showToast("Veuillez saisir le motif de l'ouverture exceptionnelle", 'error');
      return;
    }

    try {
      setIsSendingOuvertureExceptionnelle(true);
      showToast("Envoi de la demande d'ouverture exceptionnelle...", 'loading');

      const response = await api.portefeuille.demanderOuvertureExceptionnelle({
        date: getToday(),
        annee_scolaire_id: anneeActive?.id,
        motif: motifOuvertureExceptionnelle.trim()
      });

      if (response.success) {
        showToast("Demande envoyée au centre de support.", 'success');
        setShowOuvertureExceptionnelleModal(false);
        setMotifOuvertureExceptionnelle('');
        await reloadAllData();
      } else {
        showToast(response.message || "Erreur lors de l'envoi de la demande", 'error');
      }
    } catch (error) {
      console.error('Erreur demande ouverture exceptionnelle:', error);
      showToast(error.message || "Erreur lors de l'envoi de la demande", 'error');
    } finally {
      setIsSendingOuvertureExceptionnelle(false);
    }
  };

  const resetClotureModal = () => {
    setShowClotureModal(false);
    setRecapCloture(null);
    setNoteCloture('');
    setMotifEcartCloture('');
    setMontantDeclare('');
    setDateClotureEnCours(null);
  };

  const handlePrepareCloture = async (dateCaisse = getToday()) => {
    const targetDate = typeof dateCaisse === 'string' ? dateCaisse : getToday();

    try {
      setShowClotureModal(true);
      setRecapCloture(null);
      setMontantDeclare('');
      setMotifEcartCloture('');
      setDateClotureEnCours(targetDate);

      const response = await api.portefeuille.getRecapitulatifCloture(
        targetDate,
        anneeActive?.id
      );

      if (response.success) {
        setRecapCloture(response.data);
        setMontantDeclare(String(Math.round(response.data?.solde_theorique || 0)));
      } else {
        showToast(response.message || 'Erreur lors du chargement du récapitulatif', 'error');
        setShowClotureModal(false);
        setDateClotureEnCours(null);
      }
    } catch (error) {
      console.error('Erreur chargement récapitulatif:', error);
      showToast('Erreur lors du chargement du récapitulatif', 'error');
      setShowClotureModal(false);
      setDateClotureEnCours(null);
    }
  };

  const handleCloturerCaisse = async () => {
    const targetDate = dateClotureEnCours || getToday();
    const ecartCalcule = Number(montantDeclare || 0) - Number(recapCloture?.solde_theorique || 0);

    if (Math.round(ecartCalcule) !== 0 && !motifEcartCloture.trim()) {
      showToast('Veuillez renseigner le motif de l’écart avant de clôturer la caisse.', 'error');
      return;
    }

    try {
      setIsClosingCaisse(true);
      showToast('Clôture et remise en cours...', 'loading');

      const response = await api.portefeuille.cloturerCaisse(
        targetDate,
        noteCloture,
        anneeActive?.id,
        Number(montantDeclare || 0),
        motifEcartCloture.trim()
      );

      if (response.success) {
        showToast('Caisse clôturée et remise envoyée à la caisse centrale.', 'success');
        resetClotureModal();
        await reloadAllData();
      } else {
        showToast(response.message || 'Erreur lors de la clôture', 'error');
      }
    } catch (error) {
      console.error('Erreur clôture caisse:', error);
      showToast(error.message || 'Erreur lors de la clôture de la caisse', 'error');
    } finally {
      setIsClosingCaisse(false);
    }
  };

  // Tant que le statut est inconnu, aucune de ces lectures n'est vraie : la
  // barre d'action reste vide plutôt que de proposer une action qu'on retirera.
  const statutCaisseInconnu = statutCloture === null;
  const caisseJourOuverte = Boolean(!statutCaisseInconnu
    && (statutCloture?.statut === 'ouverte' || statutCloture?.est_ouverte));
  const caisseJourNonOuverte = Boolean(!statutCaisseInconnu
    && (!statutCloture?.statut || statutCloture?.statut === 'non_ouverte'));
  const caisseJourVerrouillee = Boolean(!statutCaisseInconnu
    && statutCloture?.est_cloturee && !caisseJourOuverte);
  const caisseCentraleDejaValidee = statutCaisseCentrale?.statut === 'validee';
  const caissePrecedenteNonCloturee = statutCloture?.caisse_precedente_non_cloturee || null;
  const bloqueParCaisseOubliee = filterPeriode === 'jour' && !!caissePrecedenteNonCloturee;
  const dateCaisseOubliee = caissePrecedenteNonCloturee?.date_caisse || null;
  const sortiesCaissePrincipale = Number(statsDetaillees.depensesSalaires || 0) + Number(statsDetaillees.depensesAvances || 0);
  const dotationsBudgetDepenses = Number(statsDetaillees.dotationsBudgetDepenses || 0);
  const depensesBudgetTotal = Number(statsDetaillees.depensesBudgetTotal || 0);
  const soldeBudgetDepenses = Number(statsDetaillees.soldeBudgetDepenses || 0);
  const dotationsOperationnelles = Number(
    statsDetaillees.dotationsOperationnelles ?? statsDetaillees.totalDotations ?? 0
  );
  const soldeDisponibleCaisse = Number(
    statsDetaillees.soldeCaissePrincipale ?? statsDetaillees.caisseActuelle ?? 0
  );
  const ecartCloture = Number(montantDeclare || 0) - Number(recapCloture?.solde_theorique || 0);
  const clotureAvecEcart = Math.round(ecartCloture) !== 0;

  // ============================================================================
  // DÉBUT DU RENDU - EN-TÊTE
  // ============================================================================

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full space-y-6">


        {/* ====================================================================
    EN-TÊTE AVEC FILTRES ET CALENDRIER
==================================================================== */}
        <Card className="border-0 shadow-sm overflow-visible relative z-10">

          <CardHeader className="bg-[#246990] text-white rounded-t-lg overflow-visible">

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Titre et gestionnaire */}
              <div>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <WalletCards size={32} />
                  Mon Portefeuille
                </CardTitle>
                <p className="text-white text-sm mt-1 font-semibold">
                  Gestionnaire : {currentUser?.nom} {currentUser?.prenom}
                </p>

              </div>

              {/* Actions principales avec navigation de dates */}
              <div className="flex flex-wrap items-center gap-3">

                {/* Navigation de dates — meme gabarit que l'en-tete des
                    etats de salaires : conteneur `h-8`, chevrons `h-7 w-7`,
                    champ borde transparent. Le groupe s'aligne ainsi sur les
                    boutons `size="sm"` qui le suivent ; il les depassait, avec
                    son `p-2` et ses `py-1.5`. */}
                <div className="flex h-8 items-center gap-1 rounded-md bg-white/10 px-1 backdrop-blur-sm">
                  <button
                    onClick={filterPeriode === 'mois' ? () => naviguerMois(-1) : goToPreviousDay}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors hover:bg-white/20"
                    title={filterPeriode === 'mois' ? 'Mois précédent' : 'Jour précédent'}
                  >
                    <ChevronLeft size={18} />
                  </button>

                  {/* Le champ reste un bouton : il ouvre une liste qui propose
                      les mois scolaires ET les mois hors calendrier, ce qu'un
                      selecteur natif ne sait pas faire. */}
                  <div className="relative">
                    <button
                      onClick={() => setShowCalendar(!showCalendar)}
                      title={filterPeriode === 'mois' && moisCourantChoisi?.horsCalendrier
                        ? `${moisCourantChoisi.nom} — hors calendrier scolaire`
                        : 'Choisir une date ou un mois'}
                      className={`inline-flex h-8 min-w-[132px] items-center justify-center gap-2 rounded-md border px-3 text-xs font-semibold outline-none transition focus:ring-2 focus:ring-white/70 ${moisCourantChoisi?.horsCalendrier && filterPeriode === 'mois'
                        ? 'border-amber-200/60 bg-amber-400/25 text-amber-50 hover:bg-amber-400/35'
                        : 'border-white/25 bg-transparent text-white hover:bg-white/10'}`}
                    >
                      {filterPeriode === 'mois' && moisCourantChoisi
                        ? moisCourantChoisi.nom
                        : formatDateForDisplay(selectedDate)}
                      <Calendar size={14} />
                    </button>

                    {/* Calendrier popup */}
                    {showCalendar && (
                      <div className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] p-4 min-w-[280px]">

                        {/* Sélection de date */}
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-gray-800 mb-2">Sélectionner une date</p>
                          <input
                            type="date"
                            value={formatDateForAPI(selectedDate)}
                            onChange={(e) => {
                              const newDate = new Date(e.target.value + 'T12:00:00');
                              setSelectedDate(newDate);
                              setFilterPeriode('jour');
                              setShowCalendar(false);
                            }}
                            className="w-full p-2 border border-gray-300 rounded-md text-sm text-gray-800 focus:ring-2 focus:ring-[#246990] focus:outline-none"
                          />
                        </div>

                        {/* Choix du mois.
                            « Cette semaine » et « Année scolaire » ont disparu :
                            le mois les remplace, et il est désormais borné des
                            deux côtés — l'ancien filtre ne savait lire que
                            « depuis le 1er », donc jamais un mois passé. */}
                        <div className="space-y-1 border-t pt-3">
                          <p className="mb-2 text-xs font-semibold text-gray-800">Consulter un mois</p>

                          <div className="max-h-56 space-y-1 overflow-y-auto pr-1">
                            {moisConsultables.moisScolaires.map((m) => (
                              <button
                                key={m.reference}
                                onClick={() => {
                                  setFilterPeriode('mois');
                                  setMoisChoisi(m.reference);
                                  setShowCalendar(false);
                                }}
                                className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${filterPeriode === 'mois' && String(moisChoisi) === m.reference
                                  ? 'bg-[#246990] text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                                  }`}
                              >
                                <span className="flex items-center gap-2">
                                  <CalendarRange size={16} />
                                  {m.nom}
                                </span>
                                {m.mouvements > 0 && (
                                  <span className={`text-[10px] font-semibold ${filterPeriode === 'mois' && String(moisChoisi) === m.reference ? 'text-white/70' : 'text-gray-400'}`}>
                                    {m.mouvements}
                                  </span>
                                )}
                              </button>
                            ))}

                            {/* Hors calendrier : mois grégoriens portant des
                                mouvements qu'aucun mois scolaire ne couvre.
                                Calculés, jamais proposés par principe. */}
                            {moisConsultables.horsCalendrier.length > 0 && (
                              <>
                                <p className="mt-2 border-t pt-2 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                                  Hors calendrier scolaire
                                </p>
                                {moisConsultables.horsCalendrier.map((m) => (
                                  <button
                                    key={m.reference}
                                    onClick={() => {
                                      setFilterPeriode('mois');
                                      setMoisChoisi(m.reference);
                                      setShowCalendar(false);
                                    }}
                                    className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${filterPeriode === 'mois' && String(moisChoisi) === m.reference
                                      ? 'bg-amber-600 text-white'
                                      : 'text-amber-800 hover:bg-amber-50'
                                      }`}
                                  >
                                    <span className="flex items-center gap-2">
                                      <CalendarRange size={16} />
                                      {m.nom}
                                    </span>
                                    <span className={`text-[10px] font-semibold ${filterPeriode === 'mois' && String(moisChoisi) === m.reference ? 'text-white/70' : 'text-amber-500'}`}>
                                      {m.mouvements}
                                    </span>
                                  </button>
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={filterPeriode === 'mois' ? () => naviguerMois(1) : goToNextDay}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors hover:bg-white/20"
                    title={filterPeriode === 'mois' ? 'Mois suivant' : 'Jour suivant'}
                  >
                    <ChevronRight size={18} />
                  </button>

                  <button
                    onClick={goToToday}
                    className="inline-flex h-7 items-center rounded-md px-2.5 text-xs font-semibold text-white transition-colors hover:bg-white/20"
                  >
                    Aujourd'hui
                  </button>
                </div>

                {/* Le rapport du mois sort du groupe de navigation : c'est une
                    action, pas un deplacement dans le temps. En `size="sm"` il
                    s'aligne sur les autres boutons de la barre. */}
                {filterPeriode === 'mois' && moisChoisi && (
                  <Button
                    size="sm"
                    onClick={imprimerEtatMensuel}
                    disabled={impressionEnCours}
                    title={(moisCourantChoisi?.mouvements || 0) > 200
                      ? `Rapport detaille de ${moisCourantChoisi.mouvements} mouvements — comptez une vingtaine de secondes. A n'imprimer qu'en cas de besoin reel.`
                      : "Rapport detaille du mois"}
                    className="bg-white text-[#246990] hover:bg-white/90 transition-colors disabled:opacity-50"
                  >
                    {impressionEnCours
                      ? <Loader2 size={15} className="mr-2 animate-spin" />
                      : <Download size={15} className="mr-2" />}
                    Imprimer le mois
                    {(moisCourantChoisi?.mouvements || 0) > 0 && (
                      <span className="ml-1.5 text-[10px] font-normal text-gray-400">
                        {moisCourantChoisi.mouvements}
                      </span>
                    )}
                  </Button>
                )}







                {filterPeriode === 'jour' && !bloqueParCaisseOubliee && caisseJourNonOuverte && !habiliteCaisse && (
                  <span
                    title={MESSAGE_CAISSE_RESERVEE}
                    className="inline-flex h-8 items-center rounded-md bg-white/15 px-3 text-xs font-semibold text-white"
                  >
                    <ShieldAlert size={15} className="mr-2" />
                    Caisse réservée aux profils comptables
                  </span>
                )}

                {filterPeriode === 'jour' && !bloqueParCaisseOubliee && caisseJourNonOuverte && habiliteCaisse && (
                  <Button
                    size="sm"
                    className="bg-white text-[#246990] hover:bg-white/90 transition-colors"
                    onClick={() => setShowOpenCaisseModal(true)}
                  >
                    <Unlock size={16} className="mr-2" />
                    Ouvrir la caisse
                  </Button>
                )}

                {filterPeriode === 'jour' && !bloqueParCaisseOubliee && caisseJourOuverte && (
                  <Button
                    size="sm"
                    className="bg-white text-[#246990] hover:bg-white/90 transition-colors"
                    onClick={() => setShowDemandeDotationModal(true)}
                  >
                    <Send size={16} className="mr-2" />
                    Demander dotation
                  </Button>
                )}

                {filterPeriode === 'jour' && !bloqueParCaisseOubliee && caisseJourOuverte && (
                  <Button
                    size="sm"
                    className="bg-[#c66d59] hover:bg-[#ae7467]/90 text-white transition-colors"
                    onClick={() => handlePrepareCloture()}
                  >
                    <Lock size={16} className="mr-2" />
                    Clôturer et remettre
                  </Button>
                )}

                {/* La demande d'ouverture exceptionnelle vit dans le bandeau
                    plus bas : elle y voisine avec le motif qui la justifie.
                    Un second bouton ici n'aurait porte que le libelle. */}

                {/* Une mention, pas un bouton desactive : celui-ci ne
                    s'activera jamais, et un bouton grise laisse croire le
                    contraire. */}
                {filterPeriode !== 'jour' && (
                  <span className="flex items-center gap-1.5 text-xs text-white/70">
                    <Lock size={14} />
                    Les opérations de caisse se font depuis la vue du jour.
                  </span>
                )}

              </div>
            </div>

            {/* La rangee des etats : un seul endroit ou lire l'etat de la
                caisse, du plus incertain au plus definitif. Le titre, lui,
                reste un titre. */}
            <div className="flex flex-wrap items-center gap-2 mt-2">

              {/* L'attente s'annonce. Sans cela, « inconnu » se confond avec
                  « ecran pas encore rendu », et l'on ne sait pas distinguer un
                  garde qui fonctionne d'un etat qui arrive faux. */}
              {filterPeriode === 'jour' && statutCaisseInconnu && (
                <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-white/10 text-white/80 border border-white/20">
                  <Loader2 size={14} className="mr-1 animate-spin" />
                  Vérification de la caisse…
                </span>
              )}

              {filterPeriode === 'jour' && !bloqueParCaisseOubliee && caisseJourNonOuverte && (
                <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-white/15 text-white border border-white/30">
                  <Unlock size={14} className="mr-1" />
                  Caisse non ouverte
                </span>
              )}

              {filterPeriode === 'jour' && !bloqueParCaisseOubliee && caisseJourOuverte && (
                <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-green-500/20 text-white border border-white/30">
                  <CheckCircle size={14} className="mr-1" />
                  Caisse ouverte
                </span>
              )}

              {filterPeriode === 'jour' && !bloqueParCaisseOubliee && caisseJourVerrouillee && (
                <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-red-500/20 text-white border border-white/30">
                  <Lock size={14} className="mr-1" />
                  Remise envoyée
                </span>
              )}

              {statutCaisseCentrale && (
                <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold border border-white/30 ${statutCaisseCentrale.statut === 'validee'
                  ? 'bg-green-500/20 text-white'
                  : statutCaisseCentrale.statut === 'avec_observation'
                    ? 'bg-orange-500/20 text-white'
                    : 'bg-yellow-500/20 text-white'
                  }`}>
                  {statutCaisseCentrale.statut === 'validee' ? (
                    <>
                      <CheckCircle size={14} className="mr-1" />
                      Validée ✓
                    </>
                  ) : statutCaisseCentrale.statut === 'avec_observation' ? (
                    <>
                      <AlertCircle size={14} className="mr-1" />
                      Observations ({statutCaisseCentrale.nb_observations})
                    </>
                  ) : (
                    <>
                      <Clock size={14} className="mr-1" />
                      En attente de validation
                    </>
                  )}
                </span>
              )}
            </div>
          </CardHeader>
        </Card>

        {bloqueParCaisseOubliee && (
          <div className="mt-6 rounded-lg border border-orange-300 bg-orange-50 p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-orange-100 p-2 text-orange-700">
                  <AlertCircle size={20} />
                </div>
                <div>
                  <h5 className="font-semibold text-orange-900">Caisse précedente non cloturée</h5>
                  <p className="mt-1 text-sm text-orange-800">
                    La caisse du {dateCaisseOubliee} est encore ouverte. Vous devez la cloturer et envoyer la remise
                    avant d'ouvrir ou d'utiliser la caisse du jour.
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg border border-orange-200 bg-white px-3 py-2">
                      <p className="text-xs font-semibold uppercase text-orange-700">Solde d'ouverture</p>
                      <p className="text-sm font-bold text-gray-900">{formatMontant(caissePrecedenteNonCloturee.solde_ouverture || 0)}</p>
                    </div>
                    <div className="rounded-lg border border-orange-200 bg-white px-3 py-2">
                      <p className="text-xs font-semibold uppercase text-orange-700">Encaissements</p>
                      <p className="text-sm font-bold text-gray-900">{formatMontant(caissePrecedenteNonCloturee.total_encaissements || 0)}</p>
                    </div>
                    <div className="rounded-lg border border-orange-200 bg-white px-3 py-2">
                      <p className="text-xs font-semibold uppercase text-orange-700">Dépenses</p>
                      <p className="text-sm font-bold text-gray-900">{formatMontant(caissePrecedenteNonCloturee.total_depenses || 0)}</p>
                    </div>
                    <div className="rounded-lg border border-orange-200 bg-white px-3 py-2">
                      <p className="text-xs font-semibold uppercase text-orange-700">Solde théorique</p>
                      <p className="text-sm font-bold text-gray-900">{formatMontant(caissePrecedenteNonCloturee.solde_theorique || 0)}</p>
                    </div>
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-[#246990] hover:bg-[#246990]/90 text-white flex items-center gap-2"
                onClick={() => handlePrepareCloture(dateCaisseOubliee)}
              >
                <Lock size={16} />
                Régulariser maintenant
              </Button>
            </div>
          </div>
        )}



        {/* ====================================================================
    NOTE D’INFORMATION SUR LES OPÉRATIONS FINANCIÈRES
==================================================================== */}
        <div className="mt-6 flex items-start gap-4 rounded-lg border border-[#ae7467]/20 bg-[#f9f0ee] p-4">
          <div className="flex-shrink-0 rounded-full bg-[#ae7467] p-2">
            <ReceiptText className="text-white" size={20} />
          </div>

          <div className="flex-1">
            <h5 className="font-medium text-[#ae7467]">Information importante</h5>

            {/* Cette phrase enumere ce qui traverse le portefeuille. Elle
                annoncait la cantine, les examens et les salaires a des ecoles
                qui n'ont ni l'un ni l'autre : on la compose a partir de ce qui
                est reellement ouvert plutot que de promettre en toutes lettres
                des modules absents. */}
            <p className="text-sm leading-relaxed text-gray-700">
              Tout ce qui touche à l’argent passe par ce portefeuille, sans action de votre part :
              les <strong>encaissements</strong> (scolarité, frais fixes d’inscription
              {cantineOuverte && ', frais annexes de cantine et de transport'}
              {examensOuverts && ', frais d’examens nationaux'}),
              les <strong>dotations</strong> reçues de la caisse centrale,
              les <strong>dépenses</strong> ponctuelles et fixes
              {paieOuverte && <>, les <strong>salaires</strong> et les <strong>avances sur salaire</strong></>}.
            </p>

            <ul className="mt-3 space-y-2 text-sm leading-relaxed text-gray-700">
              <li className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#ae7467]" />
                <span>
                  <span className="font-semibold text-[#ae7467]">La date n’est jamais choisie.</span>{' '}
                  Toute écriture porte la date du jour. Consulter un mois écoulé sert donc à lire,
                  non à agir : les commandes de création y sont retirées, car l’opération partirait
                  dans le mois courant sans apparaître à l’écran que vous regardez.
                </span>
              </li>

              <li className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#ae7467]" />
                <span>
                  <span className="font-semibold text-[#ae7467]">Une caisse ne se clôture qu’une fois.</span>{' '}
                  Après la remise à la caisse centrale, plus aucune écriture n’est possible sur la
                  journée : il faut demander une ouverture exceptionnelle. Les totaux remis sont
                  figés à cet instant.
                </span>
              </li>

              <li className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#ae7467]" />
                <span>
                  <span className="font-semibold text-[#ae7467]">Le justificatif fait la dépense.</span>{' '}
                  Selon le réglage de l’école, aucune dépense ne s’enregistre sans pièce jointe.
                  Pour une dépense fixe, la facture du mois est distincte du contrat du modèle :
                  le contrat dit ce qui est dû, la facture prouve ce qui a été payé.
                </span>
              </li>

              <li className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#ae7467]" />
                <span>
                  <span className="font-semibold text-[#ae7467]">Aucune ligne ne se supprime.</span>{' '}
                  Un encaissement porté sur un élève radié ensuite <strong>reste au portefeuille</strong> :
                  la ligne a déjà été validée. Une annulation relève de circonstances exceptionnelles,
                  sur justification officielle auprès de votre administrateur. Retirer une ligne après
                  une clôture creuserait un écart que l’état de caisse ne saurait plus expliquer.
                </span>
              </li>
            </ul>
          </div>
        </div>


        {filterPeriode === 'jour' && !bloqueParCaisseOubliee && caisseJourVerrouillee && (
          <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-orange-100 p-2 text-orange-700">
                  <Lock size={18} />
                </div>
                <div>
                  <h5 className="font-semibold text-orange-900">Votre caisse du jour est clôturée.</h5>
                  <p className="text-sm text-orange-800">
                    Faites l'opération le jour suivant ou demandez une ouverture exceptionnelle au centre de support.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {caisseCentraleDejaValidee && (
                  <span className="rounded-lg border border-green-200 bg-white px-3 py-2 text-sm font-semibold text-green-700">
                    Remise déjà validée centrale
                  </span>
                )}
                <Button
                  size="sm"
                  className="bg-[#246990] hover:bg-[#1d5778] text-white"
                  onClick={() => setShowOuvertureExceptionnelleModal(true)}
                >
                  <Unlock size={16} className="mr-2" />
                  {caisseCentraleDejaValidee
                    ? 'Demander annulation et réouverture'
                    : 'Demander une ouverture exceptionnelle'}
                </Button>
              </div>
            </div>
          </div>
        )}


        {/* ====================================================================
            ALERTES CAISSE CENTRALE
        ==================================================================== */}
        {statutCaisseCentrale && statutCaisseCentrale.statut === 'avec_observation' && (
          <div className="px-6">
            <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-900 mb-2">
                    ⚠️ Observations de la Caisse Centrale
                  </h4>
                  <p className="text-sm text-orange-800 mb-3">
                    Votre caisse du {new Date(statutCaisseCentrale.date_cloture_caisse).toLocaleDateString('fr-FR')} a reçu {statutCaisseCentrale.nb_observations} observation(s).
                    Veuillez y répondre pour permettre la validation.
                  </p>
                  {/* Un `alert()` natif listait les observations et se
                      terminait par « Veuillez contacter votre superviseur pour
                      y répondre » : l'application renvoyait au téléphone alors
                      que la route de réponse existait déjà côté serveur. */}
                  <Button
                    size="sm"
                    className="bg-[#c66d59] hover:bg-[#ae7467] text-white"
                    onClick={() => setShowObservationsModal(true)}
                  >
                    <MessageSquare size={16} className="mr-2" />
                    Voir et répondre
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {statutCaisseCentrale && statutCaisseCentrale.statut === 'validee' && (
          <div className="px-6">
            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-1">
                    ✅ Caisse validée par la Caisse Centrale
                  </h4>
                  <p className="text-sm text-green-800">
                    Votre caisse du {new Date(statutCaisseCentrale.date_cloture_caisse).toLocaleDateString('fr-FR')} a été validée le {new Date(statutCaisseCentrale.date_validation).toLocaleString('fr-FR')}.
                  </p>
                  {statutCaisseCentrale.note_validation && (
                    <p className="text-sm text-green-700 mt-2 italic">
                      Note: {statutCaisseCentrale.note_validation}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}


        <div className="relative">
          {(isLoading || chargementInitial) && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 border-4 border-[#246990] border-t-transparent rounded-full animate-spin"></div>
                <span className="text-[#246990] font-medium">Chargement...</span>
              </div>
            </div>
          )}



          {/* ====================================================================
    CARTES DE STATISTIQUES DÉTAILLÉES
==================================================================== */}


          <div className={`grid grid-cols-1 md:grid-cols-2 ${paieOuverte ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} gap-4 px-6`}>
            <Card className="bg-green-50 border-2 border-green-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp size={22} className="text-green-600" />
                  </div>
                  {isLoadingStats && (
                    <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-3 font-semibold">Total encaissé</p>
                <div className="space-y-2 bg-white rounded-lg p-3 border border-green-100">
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Frais fixes</span>
                    <span className="font-semibold text-blue-600 text-right">{formatMontant(statsDetaillees.fraisFixes)}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Frais scolaires</span>
                    <span className="font-semibold text-green-600 text-right">{formatMontant(statsDetaillees.fraisScolaires)}</span>
                  </div>
                  {/* Les frais d'examens ont leur ligne : ils tombaient jusqu'ici
                      dans le reliquat « frais scolaires », ou rien ne les distinguait.
                      Sans le module, aucun n'est jamais encaisse. */}
                  {examensOuverts && (
                    <div className="flex justify-between items-center gap-3 text-sm">
                      <span className="text-gray-600">Frais d'examens</span>
                      <span className="font-semibold text-[#319190] text-right">{formatMontant(statsDetaillees.fraisExamens || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Frais annexes</span>
                    <span className="font-semibold text-[#246990] text-right">{formatMontant(statsDetaillees.fraisAnnexes || 0)}</span>
                  </div>
                  {Number(statsDetaillees.fraisAnnexes || 0) > 0 && (
                    <div className="rounded-md bg-blue-50 border border-blue-100 px-2 py-1 text-[11px] text-blue-700 space-y-1">
                      <div className="flex justify-between gap-2">
                        <span>Transport</span>
                        <span className="font-semibold">{formatMontant(statsDetaillees.fraisTransport || 0)}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span>Cantine</span>
                        <span className="font-semibold">{formatMontant(statsDetaillees.fraisCantine || 0)}</span>
                      </div>
                    </div>
                  )}
                  <div className="border-t border-green-200 pt-2 mt-2 flex justify-between items-center gap-3">
                    <span className="text-gray-700 font-semibold">Total encaissé</span>
                    <span className="text-xl font-bold text-green-600 text-right">{formatMontant(statsDetaillees.totalEncaisse)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cette carte ne totalise QUE les salaires et les avances
                (`sortiesCaissePrincipale`). Sans la paie elle afficherait quatre
                zeros sous un titre qui promet des sorties : elle disparait en
                entier, pas seulement ses deux lignes. */}
            {paieOuverte && (
            <Card className="bg-orange-50 border-2 border-orange-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 bg-orange-100 rounded-lg flex items-center justify-center">
                    <TrendingDown size={22} className="text-orange-600" />
                  </div>
                  {isLoadingStats && (
                    <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-3 font-semibold">Sorties caisse principale</p>
                <div className="space-y-2 bg-white rounded-lg p-3 border border-orange-100">
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Salaires payes</span>
                    <span className="font-semibold text-purple-600 text-right">{formatMontant(statsDetaillees.depensesSalaires || 0)}</span>
                  </div>
                  {Number(statsDetaillees.depensesSalaires || 0) > 0 && (
                    <div className="rounded-md bg-purple-50 border border-purple-100 px-2 py-1 text-[11px] text-purple-700 space-y-1">
                      <div className="flex justify-between gap-2">
                        <span>Personnel</span>
                        <span className="font-semibold">{formatMontant(statsDetaillees.depensesSalairesPersonnel || 0)}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span>Enseignants</span>
                        <span className="font-semibold">{formatMontant(statsDetaillees.depensesSalairesEnseignants || 0)}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span>Hybrides</span>
                        <span className="font-semibold text-cyan-700">{formatMontant(statsDetaillees.depensesSalairesHybrides || 0)}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Avances sur salaire</span>
                    <span className="font-semibold text-amber-600 text-right">{formatMontant(statsDetaillees.depensesAvances || 0)}</span>
                  </div>
                  {Number(statsDetaillees.depensesAvances || 0) > 0 && (
                    <div className="rounded-md bg-amber-50 border border-amber-100 px-2 py-1 text-[11px] text-amber-700 space-y-1">
                      <div className="flex justify-between gap-2">
                        <span>Personnel</span>
                        <span className="font-semibold">{formatMontant(statsDetaillees.depensesAvancesPersonnel || 0)}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span>Enseignants</span>
                        <span className="font-semibold">{formatMontant(statsDetaillees.depensesAvancesEnseignants || 0)}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span>Hybrides</span>
                        <span className="font-semibold text-cyan-700">{formatMontant(statsDetaillees.depensesAvancesHybrides || 0)}</span>
                      </div>
                    </div>
                  )}
                  <div className="border-t border-orange-200 pt-2 mt-2 flex justify-between items-center gap-3">
                    <span className="text-gray-700 font-semibold">Total sorties</span>
                    <span className="text-xl font-bold text-orange-600 text-right">{formatMontant(sortiesCaissePrincipale)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            <Card className="bg-blue-50 border-2 border-blue-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ReceiptText size={22} className="text-blue-600" />
                  </div>
                  {isLoadingStats && (
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-3 font-semibold">Budget dépenses</p>
                <div className="space-y-2 bg-white rounded-lg p-3 border border-blue-100">
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Dotations budget reçues</span>
                    <span className="font-semibold text-blue-600 text-right">+{formatMontant(dotationsBudgetDepenses)}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Dépenses budget utilisées</span>
                    <span className="font-semibold text-orange-600 text-right">-{formatMontant(depensesBudgetTotal)}</span>
                  </div>
                  <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between items-center gap-3">
                    <span className="text-gray-700 font-semibold">Budget disponible</span>
                    <span className={soldeBudgetDepenses > 0 ? 'text-xl font-bold text-blue-700 text-right' : 'text-xl font-bold text-red-600 text-right'}>{formatMontant(soldeBudgetDepenses)}</span>
                  </div>
                  {dotationsBudgetDepenses > 0 && soldeBudgetDepenses <= 0 && (
                    <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2">
                      Budget dépenses épuisé. Demandez une dotation à la caisse centrale.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-purple-50 border-2 border-purple-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Scale size={22} className="text-purple-600" />
                  </div>
                  {isLoadingStats && (
                    <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-3 font-semibold">Solde caisse du jour</p>
                <div className="space-y-2 bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Encaissements</span>
                    <span className="font-semibold text-green-600 text-right">+{formatMontant(statsDetaillees.totalEncaisse || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Dotations opérationnelles</span>
                    <span className="font-semibold text-blue-600 text-right">+{formatMontant(dotationsOperationnelles)}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Sorties principales</span>
                    <span className="font-semibold text-red-600 text-right">-{formatMontant(sortiesCaissePrincipale)}</span>
                  </div>
                  <div className="border-t border-purple-200 pt-2 mt-2">
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-gray-700 font-semibold">Solde disponible</span>
                      <span className={soldeDisponibleCaisse >= 0 ? 'text-xl font-bold text-purple-700 text-right' : 'text-xl font-bold text-red-600 text-right'}>{formatMontant(soldeDisponibleCaisse)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Montant attendu à la clôture, hors budget dépenses séparé.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>



        {/* ====================================================================
    CATÉGORIE ENCAISSEMENTS - VERSION OPTIMISÉE
==================================================================== */}
        <div className="px-6">
          <Card>
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleSection('encaissements')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedSections.has('encaissements') ? (
                    <ChevronDown size={24} className="text-gray-600" />
                  ) : (
                    <ChevronRight size={24} className="text-gray-600" />
                  )}
                  <div>
                    <CardTitle className="text-xl text-green-600 flex items-center gap-2">
                      <TrendingUp size={24} />
                      Encaissements
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      suivi des paiements scolaires
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>

            {/* ✅ GARDER LE COMPOSANT MONTÉ EN PERMANENCE - juste cacher avec CSS */}
            <div className={expandedSections.has('encaissements') ? 'block' : 'hidden'}>
              <TableauEncaissements
                anneeActive={anneeActive}
                selectedDate={selectedDate}
                filterPeriode={filterPeriode}
                bornesMois={filterPeriode === 'mois' ? moisCourantChoisi : null}
                formatDateForAPI={formatDateForAPI}
                formatDateForDisplay={formatDateForDisplay}
                formatMontant={formatMontant}
              />
            </div>
          </Card>
        </div>



        {/* Toute cette categorie n'existe que si l'ecole vend des examens :
            sans le module, aucun frais d'examen n'est jamais encaisse et la
            section resterait vide en promettant une operation impossible. */}
        {examensOuverts && (
          <>
        {/* ====================================================================
            CATÉGORIE FRAIS D'EXAMENS NATIONAUX

            Ces versements existaient sans jamais entrer dans la caisse : la
            route de paiement vérifiait que la caisse était ouverte, puis
            n'y écrivait rien. Ils sont désormais tracés et comptés comme
            tout autre encaissement, mais présentés à part : ils ne relèvent
            pas de la scolarité et suivent leur propre calendrier.
        ==================================================================== */}
        <div className="px-6">
          <Card>
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleSection('fraisExamens')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedSections.has('fraisExamens') ? (
                    <ChevronDown size={24} className="text-gray-600" />
                  ) : (
                    <ChevronRight size={24} className="text-gray-600" />
                  )}
                  <div>
                    <CardTitle className="text-xl text-[#319190] flex items-center gap-2">
                      <GraduationCap size={24} />
                      Frais d'examens nationaux
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      versements encaissés pour le CEPE, le BEPC et le baccalauréat
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>

            {expandedSections.has('fraisExamens') && (
              <TableauFraisExamens
                anneeActive={anneeActive}
                selectedDate={selectedDate}
                filterPeriode={filterPeriode}
                bornesMois={filterPeriode === 'mois' ? moisCourantChoisi : null}
                formatDateForAPI={formatDateForAPI}
                formatDateForDisplay={formatDateForDisplay}
                formatMontant={formatMontant}
              />
            )}
          </Card>
        </div>
          </>
        )}



        {/* ====================================================================
            CATÉGORIE DÉPENSES
        ==================================================================== */}
        <div className="px-6">
          <Card>
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleSection('depenses')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedSections.has('depenses') ? (
                    <ChevronDown size={24} className="text-gray-600" />
                  ) : (
                    <ChevronRight size={24} className="text-gray-600" />
                  )}
                  <div>
                    <CardTitle className="text-xl text-orange-600 flex items-center gap-2">
                      <TrendingDown size={24} />
                      Charges financières
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      suivi des différentes catégories de dépenses
                    </p>

                  </div>
                </div>


              </div>
            </CardHeader>

            {expandedSections.has('depenses') && (
              <CardContent className="pt-6 space-y-6">


                {/* Remunerations et avances sur salaire : les deux seules
                    sous-sections du portefeuille qui relevent de la paie, vendue
                    separement de la comptabilite. Les depenses fixes et
                    variables qui suivent restent, elles, de la comptabilite. */}
                {paieOuverte && (
                  <>
                {/* ============================================================
                SOUS-SECTION : SALAIRES
            ============================================================ */}
                <div className="border-2 border-purple-200 rounded-lg bg-purple-50/30 mb-6">
                  <div className="p-4 bg-purple-50 border-b border-purple-200">
                    <h3 className="text-lg font-semibold text-purple-900 flex items-center gap-2">
                      <Users size={20} />
                      Rémunérations
                    </h3>
                    <p className="text-sm text-purple-700 mt-1">
                      Paiements des salaires enseignants et personnel administratif
                    </p>
                  </div>

                  <div className="p-4">

                    {isLoadingSalaires ? (
                      <div className="flex justify-center items-center h-40">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-purple-600 font-medium">Chargement...</span>
                        </div>
                      </div>
                    ) : salaires.length === 0 ? (
                      <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-purple-200">
                        <Users size={48} className="mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600 font-medium">Aucun salaire trouvé</p>
                        <p className="text-gray-500 text-sm mt-2">
                          Les salaires payés apparaîtront ici
                        </p>
                      </div>
                    ) : (




                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-purple-100 border-b border-purple-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-purple-900 uppercase">Date paiement</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-purple-900 uppercase">Employé & Poste</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-purple-900 uppercase">Période</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-purple-900 uppercase">Brut</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-purple-900 uppercase">Retenues</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-purple-900 uppercase">Avances</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-purple-900 uppercase">Net payé</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-purple-900 uppercase">Statut</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-purple-100">
                            {salaires.map((salaire) => {
                              const details = salaire.details_salaire || {};
                              const composanteLabel = getComposanteSalaireLabel(details);
                              const moisNoms = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                                'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

                              return (
                                <tr key={salaire.id} className="hover:bg-purple-50 transition-colors">
                                  {/* Date paiement */}
                                  <td className="px-4 py-3">
                                    <p className="text-sm text-gray-900 font-medium">
                                      {new Date(salaire.date_transaction).toLocaleDateString('fr-FR')}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(salaire.date_transaction).toLocaleTimeString('fr-FR', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                  </td>

                                  {/* Employé & Poste */}
                                  <td className="px-4 py-3">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {salaire.employe_prenom} {salaire.employe_nom}
                                      </p>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getProfilSalaireBadgeClass(salaire)}`}>
                                          {getProfilSalaireLabel(salaire)}
                                        </span>
                                        {composanteLabel && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                                            {composanteLabel}
                                          </span>
                                        )}
                                        {(details.type_contrat || details.mode_remuneration) && (
                                          <span className="text-xs text-gray-500">
                                            ({details.type_contrat || details.mode_remuneration})
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  {/* Période */}
                                  <td className="px-4 py-3">
                                    <p className="text-sm text-gray-700">
                                      {moisNoms[details.mois]} {details.annee}
                                    </p>
                                    {details.nombre_cours && (
                                      <p className="text-xs text-gray-500">
                                        {details.nombre_cours} cours • {details.total_heures}h
                                      </p>
                                    )}
                                    {details.jours_pointes !== undefined && (
                                      <p className="text-xs text-gray-500">
                                        {details.jours_pointes}/{details.jours_ouvres} jours
                                      </p>
                                    )}
                                  </td>

                                  {/* Brut */}
                                  <td className="px-4 py-3 text-right">
                                    <p className="text-sm font-semibold text-gray-900">
                                      {formatMontant(details.montant_brut || 0)}
                                    </p>
                                  </td>

                                  {/* Retenues */}
                                  <td className="px-4 py-3 text-right">
                                    <p className="text-sm text-orange-600">
                                      {details.montant_retenues > 0 ? '-' : ''}{formatMontant(details.montant_retenues || 0)}
                                    </p>
                                  </td>

                                  {/* Avances */}
                                  <td className="px-4 py-3 text-right">
                                    <p className="text-sm text-red-600">
                                      {details.montant_avances > 0 ? '-' : ''}{formatMontant(details.montant_avances || 0)}
                                    </p>
                                  </td>

                                  {/* Net payé */}
                                  <td className="px-4 py-3 text-right">
                                    <p className="text-sm font-bold text-purple-600">
                                      {formatMontant(salaire.montant)}
                                    </p>
                                  </td>

                                  {/* Statut */}
                                  <td className="px-4 py-3 text-center">
                                    {salaire.est_cloturee ? (
                                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        <Lock size={12} className="mr-1" />
                                        Clôturé
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <Unlock size={12} className="mr-1" />
                                        Ouvert
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination */}
                    {salaires.length > 0 && totalPagesSalaires > 1 && (
                      <div className="mt-4 flex items-center justify-between border-t pt-4">
                        <p className="text-sm text-gray-600">
                          Page {currentPageSalaires} sur {totalPagesSalaires}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPageSalaires(prev => Math.max(1, prev - 1))}
                            disabled={currentPageSalaires === 1}
                          >
                            Précédent
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPageSalaires(prev => Math.min(totalPagesSalaires, prev + 1))}
                            disabled={currentPageSalaires === totalPagesSalaires}
                          >
                            Suivant
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>



                {/* ============================================================
                    SOUS-SECTION : AVANCES SUR SALAIRE
                ============================================================ */}
                <div className="border-2 border-amber-200 rounded-lg bg-amber-50/30 mb-6">
                  <div className="p-4 bg-amber-50 border-b border-amber-200">
                    <h3 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
                      <HandCoins size={20} />
                      Avances sur salaire
                    </h3>
                    <p className="text-sm text-amber-700 mt-1">
                      Sorties de caisse remises aux employés avant le paiement du salaire
                    </p>
                  </div>

                  <div className="p-4">
                    {isLoadingTransactions ? (
                      <div className="flex justify-center items-center h-40">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-amber-700 font-medium">Chargement...</span>
                        </div>
                      </div>
                    ) : avancesSalaire.length === 0 ? (
                      <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-amber-200">
                        <HandCoins size={48} className="mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600 font-medium">Aucune avance sur salaire trouvée</p>
                        <p className="text-gray-500 text-sm mt-2">
                          Les avances accordées au personnel apparaîtront ici
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-amber-100 border-b border-amber-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-amber-900 uppercase">Date & Heure</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-amber-900 uppercase">Employé</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-amber-900 uppercase">Libellé</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-amber-900 uppercase">Montant avancé</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-amber-900 uppercase">Saisi par</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-amber-900 uppercase">Statut</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-amber-100">
                            {avancesSalaire.map((avance) => {
                              const details = avance.details_salaire || {};
                              const isHybridAvance = isHybridPortefeuilleItem(avance);
                              const employeNom = [avance.employe_prenom, avance.employe_nom]
                                .filter(Boolean)
                                .join(' ') || 'Employé non renseigné';
                              const gestionnaireNom = [avance.gestionnaire_prenom, avance.gestionnaire_nom]
                                .filter(Boolean)
                                .join(' ') || '-';

                              return (
                                <tr
                                  key={avance.id}
                                  className={`hover:bg-amber-50 transition-colors ${avance.est_cloturee ? 'bg-gray-50 opacity-75' : ''
                                    }`}
                                >
                                  <td className="px-4 py-3">
                                    <p className="text-sm text-gray-900 font-medium">
                                      {new Date(avance.date_transaction).toLocaleDateString('fr-FR')}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(avance.date_transaction).toLocaleTimeString('fr-FR', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                  </td>

                                  <td className="px-4 py-3">
                                    <p className="text-sm font-semibold text-gray-900">{employeNom}</p>
                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isHybridAvance ? 'bg-cyan-100 text-cyan-800' : getProfilSalaireBadgeClass(avance)}`}>
                                        {isHybridAvance ? 'Avance hybride' : getProfilSalaireLabel(avance)}
                                      </span>
                                      {details.salaire_reference ? (
                                        <span className="text-xs text-gray-500">Reference {formatMontant(details.salaire_reference)}</span>
                                      ) : null}
                                    </div>
                                    {avance.employe_poste && (
                                      <p className="text-xs text-gray-500 mt-1">{avance.employe_poste}</p>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <p className="text-sm text-gray-800">
                                      {avance.description || 'Avance sur salaire'}
                                    </p>
                                  </td>

                                  <td className="px-4 py-3 text-right">
                                    <p className="text-sm font-bold text-red-600">
                                      -{formatMontant(avance.montant || 0)}
                                    </p>
                                  </td>

                                  <td className="px-4 py-3">
                                    <p className="text-sm text-gray-700">{gestionnaireNom}</p>
                                  </td>

                                  <td className="px-4 py-3 text-center">
                                    {avance.est_cloturee ? (
                                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        <Lock size={12} className="mr-1" />
                                        Clôturée
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <Unlock size={12} className="mr-1" />
                                        Ouverte
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {avancesSalaire.length > 0 && totalPagesAvancesSalaire > 1 && (
                      <div className="mt-4 flex items-center justify-between border-t pt-4">
                        <p className="text-sm text-gray-600">
                          Page {currentPageAvancesSalaire} sur {totalPagesAvancesSalaire}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPageAvancesSalaire(prev => Math.max(1, prev - 1))}
                            disabled={currentPageAvancesSalaire === 1}
                          >
                            Précédent
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPageAvancesSalaire(prev => Math.min(totalPagesAvancesSalaire, prev + 1))}
                            disabled={currentPageAvancesSalaire === totalPagesAvancesSalaire}
                          >
                            Suivant
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                  </>
                )}


                {/* ============================================================
                    SOUS-SECTION : DÉPENSES FIXES À APPLIQUER

                    Le modèle vit désormais dans les Paramètres — il vaut pour
                    toute l'école, pas pour une caisse. Ne reste ici que
                    l'application : un mouvement d'argent daté du jour.
                ============================================================ */}
                <div className="border-2 border-blue-200 rounded-lg bg-blue-50/30">
                  <div className="p-4 bg-blue-50 border-b border-blue-200">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                        <Clock size={20} />
                        Dépenses fixes à appliquer ce mois
                      </h3>
                      {consulteMoisEcoule && (
                        <span className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Lock size={14} />
                          Mois écoulé — consultation seule
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-blue-700 mt-1 flex items-start gap-1.5">
                      <Info size={14} className="mt-0.5 flex-shrink-0" />
                      Les modèles (loyer, électricité, abonnements) se définissent dans
                      Outils administratifs › Paramètres › Dépenses fixes récurrentes.
                    </p>
                  </div>

                  <div className="p-4">
                    {depensesFixes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Clock size={48} className="mx-auto mb-3 text-gray-400" />
                        <p className="font-medium">Aucune dépense fixe active</p>
                        <p className="text-sm mt-1">
                          Les charges récurrentes de l'école se définissent dans les paramètres.
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-blue-100 border-b border-blue-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Dépense</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-blue-900 uppercase">Montant mensuel</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Justificatif</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Ce mois</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-blue-100">
                            {depensesFixes.map((depenseFixe) => {
                              const dejaAppliquee = isDepenseFixeAppliedThisMonth(depenseFixe);
                              const enCours = applyingDepenseFixeId === depenseFixe.id;

                              return (
                                <tr
                                  key={depenseFixe.id}
                                  className={dejaAppliquee ? 'bg-gray-50/60' : 'hover:bg-blue-50 transition-colors'}
                                >
                                  <td className="px-4 py-3">
                                    <p className={`text-sm font-medium ${dejaAppliquee ? 'text-gray-500' : 'text-gray-900'}`}>
                                      {depenseFixe.nom}
                                    </p>
                                    {depenseFixe.justificatif && (
                                      <p className="mt-0.5 max-w-xs truncate text-[11px] text-gray-500">
                                        {depenseFixe.justificatif}
                                      </p>
                                    )}
                                  </td>

                                  <td className={`px-4 py-3 text-right text-sm font-semibold tabular-nums ${dejaAppliquee ? 'text-gray-500' : 'text-gray-900'}`}>
                                    {formatMontant(depenseFixe.montant)}
                                  </td>

                                  <td className="px-4 py-3 text-center">
                                    {/* Même geste que dans les dépenses variables :
                                        une icône qui réagit au survol, et non un
                                        lien souligné. */}
                                    {depenseFixe.justificatif_fichier_url ? (
                                      <button
                                        type="button"
                                        title={depenseFixe.justificatif_fichier_nom || 'Voir le justificatif'}
                                        onClick={() => setPieceConsultee({
                                          source: 'depense-fixe',
                                          id: depenseFixe.id,
                                          titre: depenseFixe.justificatif_fichier_nom || 'Justificatif',
                                          sousTitre: depenseFixe.nom
                                        })}
                                        className="rounded-lg p-2 text-[#246990] transition-colors hover:bg-blue-100"
                                      >
                                        <FileText size={18} />
                                      </button>
                                    ) : (
                                      <span className="text-[11px] text-gray-400">—</span>
                                    )}
                                  </td>

                                  {/* Appliquee : la trace, pas le vide. Sans elle on ne
                                      saurait pas si le loyer est passe ou si le modele
                                      a disparu. La portee est celle de l'ECOLE : une
                                      collegue a pu l'appliquer depuis sa propre caisse. */}
                                  <td className="px-4 py-3 text-center">
                                    {dejaAppliquee ? (
                                      <div className="text-[11px] leading-tight">
                                        <p className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                                          <CheckCircle size={12} />
                                          Appliquée{depenseFixe.date_application
                                            ? ` le ${new Date(depenseFixe.date_application).toLocaleDateString('fr-FR')}`
                                            : ''}
                                        </p>
                                        {depenseFixe.applique_par_nom && (
                                          <p className="text-gray-500">
                                            par {depenseFixe.applique_par_prenom} {depenseFixe.applique_par_nom}
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <button
                                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${caisseJourOuverte && !consulteMoisEcoule && !enCours && !isLoading
                                          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                          : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                          }`}
                                        title={consulteMoisEcoule
                                          ? "Mois écoulé : l'application serait datée d'aujourd'hui"
                                          : !caisseJourOuverte
                                            ? 'Caisse non ouverte : action indisponible'
                                            : 'Appliquer cette dépense maintenant'}
                                        disabled={consulteMoisEcoule || !caisseJourOuverte || enCours || isLoading}
                                        onClick={() => {
                                          // « Appliquer » ECRIT une transaction datée
                                          // d'aujourd'hui : depuis un mois écoulé elle
                                          // partirait ailleurs que sur l'écran consulté.
                                          if (consulteMoisEcoule) {
                                            showToast(
                                              "Vous consultez un mois écoulé. L'application serait datée d'aujourd'hui et n'apparaîtrait pas ici.",
                                              'error'
                                            );
                                            return;
                                          }
                                          if (!caisseJourOuverte) {
                                            showToast(
                                              "Votre caisse du jour est fermée. Demandez une ouverture exceptionnelle pour appliquer une dépense fixe.",
                                              'error',
                                              { long: true, title: 'Caisse non ouverte' }
                                            );
                                            return;
                                          }
                                          if (enCours || isLoading) return;

                                          // La confirmation vit dans son propre
                                          // dialogue : elle porte un champ de fichier,
                                          // qui doit se rerendre a chaque choix.
                                          setFactureApplication(null);
                                          setDepenseFixeAAppliquer(depenseFixe);
                                        }}
                                      >
                                        {enCours ? (
                                          <>
                                            <Loader2 size={14} className="animate-spin" />
                                            Application…
                                          </>
                                        ) : (
                                          <>
                                            <CheckCircle size={14} />
                                            Appliquer
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Pagination dépenses fixes */}
                  {depensesFixes.length > 0 && totalPagesDepensesFixes > 1 && (
                    <div className="mt-4 flex items-center justify-between border-t pt-4 px-4 pb-4">
                      <p className="text-sm text-gray-600">
                        Page {currentPageDepensesFixes} sur {totalPagesDepensesFixes}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPageDepensesFixes(prev => Math.max(1, prev - 1))}
                          disabled={currentPageDepensesFixes === 1}
                        >
                          Précédent
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPageDepensesFixes(prev => Math.min(totalPagesDepensesFixes, prev + 1))}
                          disabled={currentPageDepensesFixes === totalPagesDepensesFixes}
                        >
                          Suivant
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* ============================================================
                    SOUS-SECTION : DÉPENSES VARIABLES
                ============================================================ */}
                <div className="border-2 border-orange-200 rounded-lg bg-orange-50/30">

                  <div className="p-4 bg-orange-50 border-b border-orange-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-orange-900 flex items-center gap-2">
                          <ShoppingCart size={20} />
                          Dépenses variables (ponctuelles)
                        </h3>
                        <p className="text-sm text-orange-700 mt-1">
                          Lecture budget : depenses ponctuelles cumulees jusqu'a la date selectionnee.
                        </p>
                      </div>

                      {consulteMoisEcoule ? (
                        <span className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Lock size={14} />
                          Mois écoulé — consultation seule
                        </span>
                      ) : (
                      <Button
                        size="sm"
                        className={`${caisseJourOuverte ? "bg-[#c66d59] hover:bg-[#ae7467]/90 text-white" : "bg-gray-200 text-gray-500 cursor-not-allowed hover:bg-gray-200"}`}
                        onClick={() => {
                          if (!caisseJourOuverte) {
                            showToast('Votre caisse du jour est fermee. Demandez une ouverture exceptionnelle pour ajouter une depense variable.', 'error', { long: true, title: 'Caisse non ouverte' });
                            return;
                          }
                          setShowAddDepenseModal(true);
                        }}
                        disabled={!caisseJourOuverte}
                      >
                        <Plus size={16} className="mr-2" />
                        Ajouter dépense variable
                      </Button>
                      )}
                    </div>
                  </div>

                  <div className="p-4">

                    {isLoadingDepensesVariables ? (
                      <div className="flex justify-center items-center h-40">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-orange-600 font-medium">Chargement...</span>
                        </div>
                      </div>
                    ) : depenses.length === 0 ? (
                      <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-orange-200">
                        <ShoppingCart size={48} className="mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600 font-medium">Aucune dépense variable trouvée</p>
                        <p className="text-gray-500 text-sm mt-2">
                          Aucune depense variable budget jusqu'a cette date.
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-orange-100 border-b border-orange-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-orange-900 uppercase">Date & Heure</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-orange-900 uppercase">Libellé</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-orange-900 uppercase">Montant</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-orange-900 uppercase">Justificatif</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-orange-900 uppercase">Saisi par</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-orange-100">
                            {depenses.map((depense) => (
                              <tr
                                key={depense.id}
                                className={`hover:bg-orange-50 transition-colors ${depense.est_cloturee ? 'bg-gray-50 opacity-75' : ''
                                  }`}
                              >
                                {/* Date & Heure */}
                                <td className="px-4 py-3">
                                  <div>
                                    <p className="text-sm text-gray-900 font-medium">
                                      {new Date(depense.date_transaction).toLocaleDateString('fr-FR')}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(depense.date_transaction).toLocaleTimeString('fr-FR', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                  </div>
                                </td>

                                {/* Libellé */}
                                <td className="px-4 py-3">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {depense.description}
                                    </p>
                                    {depense.depense_fixe_nom && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                        <Clock size={12} className="mr-1" />
                                        {depense.depense_fixe_nom}
                                      </span>
                                    )}
                                  </div>
                                </td>

                                {/* Montant */}
                                <td className="px-4 py-3 text-right">
                                  <p className="text-sm font-bold text-red-600">
                                    -{formatMontant(depense.montant)}
                                  </p>
                                </td>

                                {/* Justificatif.
                                    Le bouton n'avait aucun `onClick` — il
                                    avait l'air cliquable et ne faisait rien.
                                    Il testait de surcroît la NOTE texte : une
                                    dépense qui portait sa facture sans note
                                    s'affichait « manquant ». */}
                                <td className="px-4 py-3 text-center">
                                  {depense.justificatif_fichier_url ? (
                                    <button
                                      className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                                      title={depense.justificatif_fichier_nom || 'Voir le justificatif'}
                                      onClick={() => setPieceConsultee({
                                        source: 'transaction',
                                        id: depense.id,
                                        titre: depense.justificatif_fichier_nom || 'Justificatif',
                                        sousTitre: depense.description
                                      })}
                                    >
                                      <FileText size={18} />
                                    </button>
                                  ) : depense.justificatif_modele_url ? (
                                    // Une application de dépense fixe sans facture
                                    // du mois : seul le contrat est joint.
                                    <button
                                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                                      title="Pas de facture du mois — voir le contrat du modèle"
                                      onClick={() => setPieceConsultee({
                                        source: 'transaction',
                                        id: depense.id,
                                        piece: 'modele',
                                        titre: depense.justificatif_modele_nom || 'Document du modèle',
                                        sousTitre: depense.description
                                      })}
                                    >
                                      <FileText size={18} />
                                    </button>
                                  ) : depense.justificatif ? (
                                    // Une note interne, pas une pièce : le dire,
                                    // plutôt que de laisser croire à un fichier.
                                    <span
                                      className="inline-flex cursor-help text-[11px] text-gray-500"
                                      title={depense.justificatif}
                                    >
                                      Note seule
                                    </span>
                                  ) : (
                                    <div className="flex items-center justify-center">
                                      <AlertCircle size={18} className="text-orange-400" title="Justificatif manquant" />
                                    </div>
                                  )}
                                </td>

                                {/* Saisi par */}
                                <td className="px-4 py-3">
                                  <div className="text-sm text-gray-700">
                                    {depense.gestionnaire_prenom} {depense.gestionnaire_nom}
                                  </div>
                                </td>

                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>


                {/* Pagination dépenses variables */}
                {depenses.length > 0 && totalPagesDepensesVariables > 1 && (
                  <div className="mt-4 flex items-center justify-between border-t pt-4">
                    <p className="text-sm text-gray-600">
                      Page {currentPageDepensesVariables} sur {totalPagesDepensesVariables}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPageDepensesVariables(prev => Math.max(1, prev - 1))}
                        disabled={currentPageDepensesVariables === 1}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPageDepensesVariables(prev => Math.min(totalPagesDepensesVariables, prev + 1))}
                        disabled={currentPageDepensesVariables === totalPagesDepensesVariables}
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                )}


              </CardContent>
            )}
          </Card>
        </div>

        {/* ====================================================================
            HISTORIQUE COMPLET
        ==================================================================== */}
        <div className="px-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl text-[#246990]">
                    Historique des transactions
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    {transactions.length} transaction(s) - Page {currentPage} sur {totalPages}
                  </p>
                </div>



              </div>
            </CardHeader>

            <CardContent>
              {isLoadingTransactions ? (
                <div className="flex justify-center items-center h-40">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 border-4 border-[#246990] border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-[#246990] font-medium">Chargement...</span>
                  </div>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 font-medium">Aucune transaction trouvée</p>
                  <p className="text-gray-500 text-sm mt-2">
                    {searchEleve || filterSection || filterClasse
                      ? 'Essayez de modifier vos filtres'
                      : 'Les transactions apparaîtront ici'
                    }
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date & Heure</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Détails</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Montant</th>
                        {/* C'est ici qu'on revient chercher une facture, des
                            semaines plus tard. La colonne manquait. */}
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Pièce</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transactions.map((transaction) => (
                        <tr
                          key={transaction.id}
                          className={`hover:bg-gray-50 transition-colors ${transaction.est_cloturee ? 'bg-gray-50/50' : ''
                            }`}
                        >
                          {/* Type */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {getTransactionIcon(transaction.type)}
                              {getTransactionBadge(transaction)}
                            </div>
                          </td>

                          {/* Date & Heure */}
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-900 font-medium">
                              {new Date(transaction.date_transaction).toLocaleDateString('fr-FR')}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(transaction.date_transaction).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </td>

                          {/* Description */}
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-900">{transaction.description}</p>
                            {transaction.justificatif && (
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <FileText size={12} />
                                {transaction.justificatif}
                              </p>
                            )}
                          </td>

                          {/* Détails */}
                          <td className="px-4 py-3">
                            {transaction.eleve_nom && (
                              <div>
                                <p className="text-sm text-gray-900">
                                  {transaction.eleve_prenom} {transaction.eleve_nom}
                                </p>
                                <p className="text-xs text-gray-500">{transaction.classe_nom}</p>
                              </div>
                            )}
                            {transaction.employe_nom && (
                              <div>
                                <p className="text-sm text-gray-900 flex items-center gap-1">
                                  <Users size={14} className="text-gray-400" />
                                  {transaction.employe_prenom} {transaction.employe_nom}
                                </p>
                              </div>
                            )}
                            {!transaction.eleve_nom && !transaction.employe_nom && (
                              <p className="text-sm text-gray-500">—</p>
                            )}
                          </td>

                          {/* Montant */}
                          <td className="px-4 py-3 text-right">
                            <p className={`text-sm font-bold ${transaction.type === 'encaissement' ? 'text-green-600' : 'text-red-600'
                              }`}>
                              {transaction.type === 'encaissement' ? '+' : '-'}{formatMontant(transaction.montant)}
                            </p>
                          </td>

                          {/* Pièce jointe.
                              La facture du mois d'abord ; à défaut le contrat
                              du modèle, en gris — il ne prouve pas le paiement. */}
                          <td className="px-4 py-3 text-center">
                            {transaction.justificatif_fichier_url ? (
                              <button
                                className="rounded-lg p-1.5 text-[#246990] transition-colors hover:bg-[#eef7fb]"
                                title={transaction.justificatif_fichier_nom || 'Voir la pièce'}
                                onClick={() => setPieceConsultee({
                                  source: 'transaction',
                                  id: transaction.id,
                                  titre: transaction.justificatif_fichier_nom || 'Justificatif',
                                  sousTitre: transaction.description
                                })}
                              >
                                <FileText size={16} />
                              </button>
                            ) : transaction.justificatif_modele_url ? (
                              <button
                                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100"
                                title="Pas de facture du mois — voir le contrat du modèle"
                                onClick={() => setPieceConsultee({
                                  source: 'transaction',
                                  id: transaction.id,
                                  piece: 'modele',
                                  titre: transaction.justificatif_modele_nom || 'Document du modèle',
                                  sousTitre: transaction.description
                                })}
                              >
                                <FileText size={16} />
                              </button>
                            ) : (
                              <span className="text-[11px] text-gray-300">—</span>
                            )}
                          </td>

                          {/* Statut */}
                          <td className="px-4 py-3 text-center">
                            {transaction.est_cloturee ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <Lock size={12} className="mr-1" />
                                Clôturée
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <Unlock size={12} className="mr-1" />
                                Ouverte
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Pagination historique */}
              {transactions.length > 0 && totalPagesHistorique > 1 && (
                <div className="mt-6 flex items-center justify-between border-t pt-4">
                  <p className="text-sm text-gray-600">
                    Page {currentPageHistorique} sur {totalPagesHistorique}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPageHistorique(prev => Math.max(1, prev - 1))}
                      disabled={currentPageHistorique === 1}
                    >
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPageHistorique(prev => Math.min(totalPagesHistorique, prev + 1))}
                      disabled={currentPageHistorique === totalPagesHistorique}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              )}


            </CardContent>
          </Card>
        </div>

      </div>

      {/* ====================================================================
          MODAL : OUVERTURE DE CAISSE
      ==================================================================== */}
      {showOpenCaisseModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full overflow-hidden">
            <div className="bg-[#246990] p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-white/80">
                  Caisse journalière
                </p>
                <h3 className="text-xl font-semibold text-white flex items-center gap-3">
                  <Unlock size={24} />
                  Ouvrir la caisse du jour
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowOpenCaisseModal(false);
                  setNoteOuverture('');
                }}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                disabled={isOpeningCaisse}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                La caisse doit être ouverte avant tout encaissement, dépense, avance sur salaire ou paiement de salaire.
                La clôture du soir créera ensuite une remise vers la caisse centrale.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de caisse
                  </label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                    {getToday()}
                  </div>
                </div>

                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  <p className="font-semibold">Ouverture à 0 FCFA</p>
                  <p className="mt-1 text-xs leading-relaxed">
                    Les fonds disponibles viennent uniquement d'une dotation tracée depuis la caisse centrale.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note d'ouverture (optionnelle)
                </label>
                <textarea
                  value={noteOuverture}
                  onChange={(e) => setNoteOuverture(e.target.value)}
                  placeholder="Ex: fond de caisse remis en début de journée..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowOpenCaisseModal(false);
                    setNoteOuverture('');
                  }}
                  disabled={isOpeningCaisse}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-[#246990] hover:bg-[#1d5778] text-white"
                  onClick={handleOpenCaisse}
                  disabled={isOpeningCaisse}
                >
                  {isOpeningCaisse ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Ouverture...
                    </>
                  ) : (
                    <>
                      <Unlock size={18} className="mr-2" />
                      Ouvrir la caisse
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          MODAL : CLÔTURE DE CAISSE
      ==================================================================== */}
      {showOuvertureExceptionnelleModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full overflow-hidden">
            <div className="bg-[#246990] p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-white/80">
                  Demande sensible
                </p>
                <h3 className="text-xl font-semibold text-white flex items-center gap-3">
                  <Unlock size={24} />
                  {caisseCentraleDejaValidee
                    ? 'Annulation centrale et réouverture'
                    : 'Ouverture exceptionnelle de caisse'}
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowOuvertureExceptionnelleModal(false);
                  setMotifOuvertureExceptionnelle('');
                }}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                disabled={isSendingOuvertureExceptionnelle}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                {caisseCentraleDejaValidee
                  ? "Cette demande sera envoyée au Centre de Support en procédure renforcée. Si elle est approuvée, la validation centrale sera annotée en observation, puis la caisse sera rouverte."
                  : "Cette demande sera envoyée au Centre de Support. La caisse ne sera rouverte qu'après validation. Les transactions du jour redeviendront modifiables uniquement après approbation."}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Date de caisse</p>
                  <p className="mt-2 font-bold text-gray-900">{getToday()}</p>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Solde caisse du jour</p>
                  <p className="mt-2 font-bold text-blue-900">{formatMontant(soldeDisponibleCaisse)}</p>
                </div>
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Statut actuel</p>
                  <p className="mt-2 font-bold text-orange-900">{statutCloture?.statut || 'Clôturée'}</p>
                </div>
                <div className={`rounded-lg border p-4 ${caisseCentraleDejaValidee
                  ? 'border-red-200 bg-red-50'
                  : 'border-emerald-200 bg-emerald-50'
                  }`}>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${caisseCentraleDejaValidee ? 'text-red-700' : 'text-emerald-700'
                    }`}>
                    Niveau
                  </p>
                  <p className={`mt-2 font-bold ${caisseCentraleDejaValidee ? 'text-red-900' : 'text-emerald-900'
                    }`}>
                    {caisseCentraleDejaValidee ? 'Annulation centrale' : 'Support'}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Encaissements clôturés</p>
                    <p className="font-bold text-green-700">{formatMontant(statutCloture?.total_encaissements || 0)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Dépenses clôturées</p>
                    <p className="font-bold text-red-700">{formatMontant(statutCloture?.total_depenses || 0)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Solde attendu</p>
                    <p className="font-bold text-[#246990]">{formatMontant(statutCloture?.solde_theorique || 0)}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motif de la demande *
                </label>
                <textarea
                  value={motifOuvertureExceptionnelle}
                  onChange={(e) => setMotifOuvertureExceptionnelle(e.target.value)}
                  placeholder="Ex: erreur de saisie constatée après clôture, transaction oubliée, correction contrôlée..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                />
              </div>

              <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
                {caisseCentraleDejaValidee
                  ? "La validation support annulera la validation centrale en observation, conservera la trace de l'ancien contrôle central, puis rouvrira la caisse du jour pour correction."
                  : "La validation support annotera la remise centrale en observation et rouvrira la caisse du jour."}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowOuvertureExceptionnelleModal(false);
                    setMotifOuvertureExceptionnelle('');
                  }}
                  disabled={isSendingOuvertureExceptionnelle}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-[#246990] hover:bg-[#1d5778] text-white"
                  onClick={handleDemandeOuvertureExceptionnelle}
                  disabled={isSendingOuvertureExceptionnelle}
                >
                  {isSendingOuvertureExceptionnelle ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Envoyer la demande
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ModalCloturerCaisse
        isOpen={showClotureModal}
        onClose={resetClotureModal}
        dateCloture={dateClotureEnCours}
        dateDuJour={getToday()}
        recap={recapCloture}
        montantDeclare={montantDeclare}
        setMontantDeclare={setMontantDeclare}
        noteCloture={noteCloture}
        setNoteCloture={setNoteCloture}
        motifEcart={motifEcartCloture}
        setMotifEcart={setMotifEcartCloture}
        ecart={ecartCloture}
        avecEcart={clotureAvecEcart}
        isClosing={isClosingCaisse}
        onSubmit={handleCloturerCaisse}
        formatMontant={formatMontant}
      />

      {/* ====================================================================
          MODAL : NOTIFICATION MENSUELLE DÉPENSES FIXES
      ==================================================================== */}
      {showMonthlyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-blue-600 p-6 rounded-t-lg flex items-center justify-between sticky top-0 z-10">
              <h3 className="text-xl font-semibold text-white flex items-center gap-3">
                <Clock size={24} />
                Dépenses fixes mensuelles en attente
              </h3>
              <button
                onClick={() => {
                  setShowMonthlyModal(false);
                  setMonthlyDepenses([]);
                  setAdjustingDepenses(false);
                }}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <p className="text-blue-900 font-medium">
                  Les dépenses fixes suivantes sont en attente d'application pour ce mois.
                </p>
                <p className="text-blue-800 text-sm mt-2">
                  Vous pouvez les appliquer immédiatement, ajuster les montants, ou reporter au mois prochain.
                </p>
              </div>

              {/* Liste des dépenses */}
              <div className="space-y-3">
                {monthlyDepenses.map((depense, index) => (
                  <div key={depense.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{depense.nom}</p>
                        {depense.lastApplied && (
                          <p className="text-xs text-gray-500 mt-1">
                            Dernière application : {new Date(depense.lastApplied).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                      {adjustingDepenses ? (
                        <Input
                          type="number"
                          value={depense.montant}
                          onChange={(e) => {
                            const updated = [...monthlyDepenses];
                            updated[index].montant = parseFloat(e.target.value) || 0;
                            setMonthlyDepenses(updated);
                          }}
                          className="w-32 text-right font-bold"
                        />
                      ) : (
                        <p className="text-lg font-bold text-blue-600">
                          {formatMontant(depense.montant)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-gray-900">Total à appliquer</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatMontant(monthlyDepenses.reduce((sum, d) => sum + d.montant, 0))}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                {!adjustingDepenses ? (
                  <>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={async () => {
                        try {
                          const response = await api.depensesFixes.annulerMois(anneeActive?.id);
                          if (response.success) {
                            alert('Dépenses reportées au mois prochain');
                            setShowMonthlyModal(false);
                            setMonthlyDepenses([]);
                          }
                        } catch (error) {
                          alert('Erreur lors du report');
                        }
                      }}
                    >
                      Reporter au mois prochain
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setAdjustingDepenses(true)}
                    >
                      <Edit size={18} className="mr-2" />
                      Ajuster les montants
                    </Button>
                    <Button
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={async () => {
                        try {
                          setIsLoading(true);
                          showToast('Application des dépenses fixes en cours...', 'loading');

                          const response = await api.depensesFixes.appliquerMensuelles(
                            monthlyDepenses,
                            anneeActive?.id,
                            filterPeriode === 'mois' ? moisChoisi : null
                          );

                          if (response.success) {
                            const montantTotal = response.data?.montant_total ?? monthlyDepenses.reduce((sum, depense) => sum + Number(depense.montant || 0), 0);
                            const successMessage = [
                              `${response.data?.nb_depenses || monthlyDepenses.length} dépense(s) fixe(s) appliquée(s).`,
                              '',
                              `Montant total : ${formatMontant(montantTotal)}`,
                              'La dépense a été ajoutée au portefeuille et le budget dépenses a été mis à jour.'
                            ].join('\n');
                            showToast(successMessage, 'success', { long: true, title: 'Dépenses fixes appliquées' });
                            setShowMonthlyModal(false);
                            setMonthlyDepenses([]);
                            await reloadAllData();
                          }
                        } catch (error) {
                          console.error('Erreur application dépenses:', error);
                          showToast(
                            error.message || 'Erreur lors de l\'application des dépenses fixes.',
                            'error',
                            { long: true, title: 'Application impossible' }
                          );
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Application...' : 'Appliquer maintenant'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setAdjustingDepenses(false)}
                    >
                      Annuler
                    </Button>
                    <Button
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => setAdjustingDepenses(false)}
                    >
                      <Save size={18} className="mr-2" />
                      Valider les ajustements
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ====================================================================
    MODAL : AJOUTER UNE DEPENSE VARIABLE
==================================================================== */}
      {/* ====================================================================
    MODAL : AJOUTER UNE DÉPENSE VARIABLE
    Sur les primitives Dialog, comme l'exige DESIGN-SYSTEM.md — il était
    monté à la main sur un `fixed inset-0`, avec des boutons de pied de
    largeurs différentes.
==================================================================== */}
      <Dialog
        open={showAddDepenseModal}
        onOpenChange={(ouvert) => {
          if (ouvert) return;
          setShowAddDepenseModal(false);
          setNewDepense({ description: '', montant: '', justificatif: '', justificatif_fichier: null });
        }}
      >
        <DialogContent className="flex w-[95vw] max-w-5xl flex-col gap-0 overflow-hidden p-0 !max-h-[92vh]">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <ShoppingCart size={18} />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Ajouter une dépense ponctuelle
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                Une sortie unique, tirée de votre budget dépenses et datée d'aujourd'hui.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Nom de la dépense <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Ex : achat de craie, réparation…"
                  value={newDepense.description}
                  onChange={(e) => setNewDepense({ ...newDepense, description: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Montant <span className="text-red-500">*</span>
                </label>
                <div className="flex overflow-hidden rounded-lg border border-gray-300">
                  <Input
                    type="number"
                    placeholder="0"
                    min="0"
                    step="100"
                    value={newDepense.montant}
                    onChange={(e) => setNewDepense({ ...newDepense, montant: e.target.value })}
                    className="rounded-none border-0 focus:ring-0"
                  />
                  <span className="inline-flex items-center border-l border-gray-200 bg-gray-50 px-4 text-sm font-semibold text-gray-600">
                    FCFA
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Note interne</label>
              <textarea
                placeholder="Motif, contexte ou référence complémentaire…"
                rows={3}
                value={newDepense.justificatif}
                onChange={(e) => setNewDepense({ ...newDepense, justificatif: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
              />
            </div>

            <JustificatifInput
              required={reglagesDepenses.justificatif_requis_creation}
              file={newDepense.justificatif_fichier}
              onFileChange={(file) => setNewDepense({ ...newDepense, justificatif_fichier: file })}
              title="Justificatif de dépense"
              description={reglagesDepenses.justificatif_requis_creation
                ? "Facture, reçu ou photo du document. Obligatoire pour enregistrer la dépense."
                : "Facture, reçu ou photo du document."}
              accentColor="#246990"
            />
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED_CLASSES}
              disabled={isLoading}
              onClick={() => {
                setShowAddDepenseModal(false);
                setNewDepense({ description: '', montant: '', justificatif: '', justificatif_fichier: null });
              }}
            >
              Annuler
            </Button>
            <Button
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50`}
              disabled={isLoading}
              onClick={async () => {
                if (!newDepense.description || !newDepense.montant) {
                  showToast('Veuillez renseigner le nom et le montant de la dépense.', 'error');
                  return;
                }
                if (parseFloat(newDepense.montant) <= 0) {
                  showToast('Le montant doit être supérieur à 0 FCFA.', 'error');
                  return;
                }
                if (reglagesDepenses.justificatif_requis_creation && !newDepense.justificatif_fichier) {
                  showToast("Le justificatif est obligatoire pour enregistrer une dépense.", 'error');
                  return;
                }
                try {
                  setIsLoading(true);
                  showToast('Enregistrement de la dépense en cours…', 'loading');
                  const response = await api.portefeuille.ajouterDepense({
                    mois_consulte: filterPeriode === 'mois' ? moisChoisi : null,
                    description: newDepense.description,
                    montant: parseFloat(newDepense.montant),
                    justificatif: newDepense.justificatif,
                    justificatif_fichier: newDepense.justificatif_fichier,
                    annee_scolaire_id: anneeActive?.id
                  });
                  if (response.success) {
                    showToast('Dépense ajoutée avec succès.', 'success');
                    setNewDepense({ description: '', montant: '', justificatif: '', justificatif_fichier: null });
                    setShowAddDepenseModal(false);
                    await reloadAllData();
                  } else {
                    showToast(response.message || "Erreur lors de l'ajout de la dépense", 'error');
                  }
                } catch (error) {
                  console.error('Erreur ajout dépense :', error);
                  showToast(error.message || "Erreur lors de l'ajout de la dépense", 'error');
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              {isLoading ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====================================================================
    MODAL : DEMANDE DE DOTATION
==================================================================== */}
      <ModalDemanderDotation
        isOpen={showDemandeDotationModal}
        onClose={() => setShowDemandeDotationModal(false)}
        demande={demandeDotation}
        setDemande={setDemandeDotation}
        isSending={isSendingDemandeDotation}
        onSubmit={handleDemanderDotation}
        moisScolaires={moisScolairesDotation}
      />

      {/* ====================================================================
          DIALOG DE CONFIRMATION
      ==================================================================== */}
      {/* Application d'une dépense fixe.
          Un Dialog et non un ConfirmDialog : on saisit une pièce, on ne se
          contente pas d'acquiescer. */}
      <Dialog
        open={Boolean(depenseFixeAAppliquer)}
        onOpenChange={(ouvert) => {
          if (ouvert) return;
          setDepenseFixeAAppliquer(null);
          setFactureApplication(null);
          setVoirContratModele(false);
        }}
      >
        <DialogContent className="flex w-[95vw] max-w-3xl flex-col gap-0 overflow-hidden p-0 !max-h-[92vh]">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Clock size={18} />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Appliquer la dépense fixe
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                Le montant sortira de votre budget dépenses, daté d'aujourd'hui.
              </DialogDescription>
            </div>
          </DialogHeader>

          {depenseFixeAAppliquer && (
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {/* Ce qu'on s'apprête à engager, lisible d'un coup d'oeil. */}
              <div className="flex items-center justify-between gap-4 rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {depenseFixeAAppliquer.nom}
                  </p>
                  <p className="text-[11px] text-gray-600">Dépense fixe mensuelle</p>
                </div>
                <p className="flex-shrink-0 text-lg font-bold tabular-nums text-[#246990]">
                  {formatMontant(depenseFixeAAppliquer.montant)}
                </p>
              </div>

              <JustificatifInput
                required={reglagesDepenses.justificatif_requis_application}
                file={factureApplication}
                onFileChange={setFactureApplication}
                title="Facture du mois"
                description={
                  reglagesDepenses.justificatif_requis_application
                    ? "La facture réellement payée ce mois-ci. Obligatoire."
                    : "La facture payée ce mois-ci, si vous l'avez sous la main."
                }
                accentColor="#246990"
              />

              {/* Le contrat du modèle reste consultable : il dit ce qui est dû,
                  pas ce qui a été payé. */}
              {depenseFixeAAppliquer.justificatif_fichier_url && (
                <button
                  type="button"
                  onClick={() => setVoirContratModele(true)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#246990] hover:underline"
                >
                  <FileText size={12} />
                  Voir le document du modèle
                </button>
              )}
            </div>
          )}

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED_CLASSES}
              disabled={applyingDepenseFixeId === depenseFixeAAppliquer?.id}
              onClick={() => {
                setDepenseFixeAAppliquer(null);
                setFactureApplication(null);
              }}
            >
              Annuler
            </Button>
            <Button
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50`}
              disabled={
                applyingDepenseFixeId === depenseFixeAAppliquer?.id ||
                (reglagesDepenses.justificatif_requis_application && !factureApplication)
              }
              onClick={() => appliquerDepenseFixe()}
            >
              {applyingDepenseFixeId === depenseFixeAAppliquer?.id ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Application…
                </>
              ) : (
                'Appliquer'
              )}
            </Button>
          </DialogFooter>

          {/* IMBRIQUEE dans le dialogue, pas posee a cote : rendue en frere,
              elle s'affichait sous l'assombrissement de celui-ci et tout se
              figeait. Radix n'empile correctement que ce qui est dans l'arbre. */}
          <VisionneuseJustificatif
            ouvert={voirContratModele}
            onFermer={() => setVoirContratModele(false)}
            source="depense-fixe"
            id={depenseFixeAAppliquer?.id}
            titre={depenseFixeAAppliquer?.justificatif_fichier_nom || 'Document du modèle'}
            sousTitre={depenseFixeAAppliquer ? `Contrat de « ${depenseFixeAAppliquer.nom} »` : ''}
          />
        </DialogContent>
      </Dialog>

      <VisionneuseJustificatif
        ouvert={Boolean(pieceConsultee)}
        onFermer={() => setPieceConsultee(null)}
        source={pieceConsultee?.source}
        id={pieceConsultee?.id}
        piece={pieceConsultee?.piece}
        titre={pieceConsultee?.titre}
        sousTitre={pieceConsultee?.sousTitre}
      />

      <ModalObservationsCaisse
        ouvert={showObservationsModal}
        onFermer={() => setShowObservationsModal(false)}
        caisseCentraleId={statutCaisseCentrale?.id}
        dateCaisse={statutCaisseCentrale?.date_cloture_caisse}
        onRepondu={() => caisseDuJour.rafraichir?.()}
      />

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setConfirmDialogConfig({ title: '', description: '', onConfirm: null });
        }}
        onConfirm={() => {
          if (confirmDialogConfig.onConfirm) {
            confirmDialogConfig.onConfirm();
          }
        }}
        title={confirmDialogConfig.title}
        description={confirmDialogConfig.description}
        confirmText="Confirmer"
        cancelText="Annuler"
        type="warning"
        isLoading={isLoading}
      />


      {/* Toast notifications */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}

      <LongToast
        show={longToast.show}
        title={longToast.title}
        message={longToast.message}
        type={longToast.type}
        onClose={() => setLongToast({ show: false, title: '', message: '', type: 'error' })}
      />
      {/* ====================================================================
          LOADING OVERLAY GLOBAL
      ==================================================================== */}



    </div>
  );
};

export default PortefeuilleRefonte;
